/**
 * AI FP (Factory Planning) Scenario Planning
 * FP 모듈 전용 — 공용 컨테이너(AiScenarioPlanning)에 FP_MODULE 설정 객체로 위임한다.
 *
 * ⚠️ 데모 우선 모드(D-013) — FP 백엔드(02-PHASE3) 연동 전까지의 1차 골격:
 *   - 🔄 2026-09-09 (F-18) fetchTemplates 신설 — DB 표준 시나리오 10장이 카드
 *     격자에 오르고, 정적 FP_MODULE.templates 는 **화면 경로에서 폴백으로만** 남는다
 *     (컨테이너가 hasDynTemplates 로 갈라 tplList 를 dynTemplates 로 대체한다 —
 *     AiScenarioPlanning.js:1078·:1465). 에이전트·챗 경로(:5496 등)는 여전히 templates 를 본다.
 *   - 현황 KPI·AI 추천·엔진 실행·비교/확정 실데이터는 백단 연동 후 활성화.
 *   - v1 노출 레버(D-014): 계획 정책 4 category + 자원 efficiency/work_hour_per_day.
 *     편집기는 공용 ScenarioParamDefsFields — 순서 의미 규칙은 "N순위 select 행"으로
 *     축약 표현한다(데모 한정, 정본: saas-fp/docs/fp-ai-scenario/docs/04-REF-PARAM-SURFACE.md §2).
 *   - ⚠️ FP 파라미터에 param_key "RC"/"periods"/"min_lot_size"/"max_lot_size" 를 도입하지 말 것 —
 *     공용 mpScenarioParamUtil 이 전 모듈에 강제 적용되어 RC 도입 시 efficiency 등이 조용히 드롭된다.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Card, CardContent, CircularProgress, IconButton, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";

// FP 32개 화면이 공유하는 계획범위+계획일자+버전 선택기. <PlanScope> 를 내장하고 있고,
// AI 격리 버전(z delFlg='Y')까지 걸러 준다([D-029]) — 여기서 새로 만들 것이 없다.
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";

import i18n from "@plannel/utils/i18n";
import scenarioService from "@plannel/services/ai/scenarioService";
import { rememberModuleCatalog } from "./scenarioParamUtil";
import { FP_DOWNTIME_KEYS, FpDowntimeFields, fpAttachDowntimeFormat, fpDowntimeDefForEditor, fpDowntimeEngineValue, fpDowntimePut, fpFormatDowntime, fpIsDowntimeValue, fpPlanPeriodOf, useFpDowntimeSlot } from "./fpDowntimeFields";   // [D-120] 설비 정지 기간(비조업 설정) 위젯
import ScenarioParamDefsFields from "./scenarioParamDefsFields";   // 공용 파라미터 편집기 — FP 는 이걸 **그대로 재사용**하고 입력만 정규화한다([Q-069], 아래 훅).
// 대상 선택기 — 공용 프리미티브를 **한 줄도 고치지 않고** 그대로 쓴다 (F-17).
//   AiTargetMultiSelect 는 검색·그룹·체크박스를, AiSegmentedControl 은 축 전환을 맡는다.
import { AiTargetMultiSelect } from "@plannel/components/ai/AiTargetMultiSelect";
import AiSegmentedControl from "./AiSegmentedControl";
// 워치독 권장 임계. 값을 베끼지 않고 가져다 쓴다 — 두 곳에 적으면 한쪽만 바뀐다.
import { SSE_IDLE_TIMEOUT_MS } from "./engineSseWatchdog";
// 서버 병행 i18n 계약(`{field}_key` · `{key,args}` 수치 셀) 렌더 프리미티브.
//   ⚠️ 2026-09-22 upstream 머지 전까지 FP 만 이걸 안 썼다 — 서버는 이미 병행 필드를 보내는데
//      화면이 ko 원문만 읽어 en/ja/zh/vi 에서 '일'·'분'·'회' 가 그대로 샜다.
//   (KPI 번역 셀 `cellText` 는 현황판 파일이 쓴다 — fpStatusBoard.buildFpStatusKpiCards.)
import { tKeyOr } from "./aiFieldText";
// 비교 탭 「카드 관측 지표」용 공용 토큰([Q-079]). 🔴 공용 컨테이너(AiScenarioPlanning)에서는
// import 할 수 없다 — 그 파일이 이 파일을 import 하고(:32) export 도 default 하나뿐이다.
//   C 는 이 파일의 로컬 안내 카드 팔레트(아래 `const C`)와 이름이 겹쳐 UIC 로 받는다.
import {
  C as UIC, KPI_EMPTY_DASH, STATUS_CARD_GRID_SX, StatusEmpty, StatusEmptyPanel,
  StatusInsightCard, StatusItemHeader, StatusItemList, StatusItemRow, StatusKpiSection,
  StatusNotice, StatusPendingTag, StatusSection, useLastRunSummary,
} from "./scenarioStatusUi";
import { FONT } from "@plannel/components/ai/typographyTokens";
import { FeasibilityHeroBadges, feasWarnCount } from "./CompareFeasibilityWarnings";
// [D-109] 현황판 부품 — FP 전용 파일이다(이 파일을 import 하지 않는다: 순환 금지).
import {
  FP_SEVERITY, FP_SEVERITY_ORDER, FpCompareBar, FpKpiBreakdown, FpLoadTrend, FpSignalCards,
  FpTopProblems, buildFpStatusKpiCards, fpCardColumns, fpCardGridSx, fpChip, fpFix, fpFmt, fpKpiColumns,
  fpKpiCardsForRender, fpProblemTpLabel,
  useFpElementWidth,
} from "./fpStatusBoard";

// ── 색상(안내 카드 전용, IP 팔레트와 동일 계열) ─────────────────────────────
const C = {
  primary: "#185FA5",
  text2: "#4B5563",
  border: "#E5E7EB",
};

// ── v1 정책 레버 코드 목록 — 정본: 04-REF-PARAM-SURFACE.md §2.1 (tb_fp_comn_code 시드) ──
// 라벨 i18n 키 = 코드 문자열 자체(언어팩 fp 섹션에 전 코드 존재 — RULE_* 블록).
const FP_SEQ_RULES = [
  "RULE_PLAN_SEQ_ORDER_PRIORITY", "RULE_PLAN_SEQ_ITEM_PRIORITY", "RULE_PLAN_SEQ_DUE_DATE",
  "RULE_PLAN_SEQ_CUSTOMER_PRIORITY", "RULE_PLAN_SEQ_QUANTITY", "RULE_PLAN_SEQ_INVENTORY_CODE",
  "RULE_PLAN_SEQ_DUE_WEEK", "RULE_PLAN_SEQ_DUE_MONTH", "RULE_PLAN_SEQ_FORECAST_TYPE",
  "RULE_PLAN_SEQ_WORK_ORDER_CODE",
];
const FP_PRODUCTION_RULES = [
  "RULE_SCHEDULE_END_DATE", "RULE_BACKWARD_TIME_WINDOW", "RULE_FORWARD_TIME_WINDOW",
  "RULE_DUE_DATE", "RULE_ALTERNATE_POLICY", "RULE_RESOURCE_LAYER", "RULE_EARLIEST_DATE",
  "RULE_LATEST_DATE", "RULE_PRIORITY", "RULE_CODE", "RULE_DUE_DATE_CLOSEST",
  "RULE_EARLIEST_END_DATE", "RULE_EARLIEST_START_DATE", "RULE_LATEST_END_DATE",
  "RULE_LATEST_START_DATE", "RULE_BATCH_JOB", "RULE_ORDER_ITEM_BATCH_JOB",
  "RULE_ORDER_ITEM_GROUP_BATCH_JOB", "RULE_ACTIVITY_ITEM_BATCH_JOB",
  "RULE_ACTIVITY_ITEM_GROUP_BATCH_JOB", "RULE_JOB_CHANGE",
];
const FP_STOCK_RULES = [
  "RULE_STOCK_PEGGING", "RULE_STOCK_INFINITE", "RULE_STOCK_HARD_INFINITE", "RULE_STOCK_DATE",
  "RULE_STOCK_QUANTITY", "RULE_STOCK_PRIORITY", "RULE_STOCK_CODE", "RULE_STOCK_NEAR",
  "RULE_STOCK_INFO", "RULE_STOCK_QUANTITY_DESC",
];
const ruleOpts = (codes) => codes.map((c) => ({ value: c, label_key: c }));

// 공용 ScenarioParamDefsFields 가 읽는 param_def 스키마(param_key/label_key/type/options/min/max/step/unit).
// 순서 의미 규칙(오더 정렬 등)은 공용 편집기에 순서 입력 위젯이 없어 "N순위 select 행"으로 축약(데모 한정).
const FP_PARAM_DEFS = [
  { param_key: "plan_strategy", label_key: "fpScnPlanStrategy", type: "select",
    options: [
      { value: "FORWARD", label_key: "fpScnStrategyForward" },
      { value: "BACKWARD", label_key: "fpScnStrategyBackward" },
    ] },
  { param_key: "order_seq_rule_1", label_key: "fpScnParamOrderSeqRule1", type: "select", options: ruleOpts(FP_SEQ_RULES) },
  { param_key: "order_seq_rule_2", label_key: "fpScnParamOrderSeqRule2", type: "select", options: ruleOpts(FP_SEQ_RULES) },
  { param_key: "production_rule_1", label_key: "fpScnParamProductionRule1", type: "select", options: ruleOpts(FP_PRODUCTION_RULES) },
  { param_key: "stock_use_rule_1", label_key: "fpScnParamStockUseRule1", type: "select", options: ruleOpts(FP_STOCK_RULES) },
  { param_key: "efficiency", label_key: "fpScnParamEfficiency", type: "number", min: 0.1, max: 3, step: 0.05, unit: "x" },
  { param_key: "work_hour_per_day", label_key: "fpScnParamWorkHour", type: "number", min: 1, max: 24, step: 1, unit: "h" },
];

// ── FP 현황 탭 — 계획범위·기준 버전 선택 [D-031] ─────────────────────────────
// IP(IpExtraInitContent)의 부모 계약을 승계한다:
//   onReadyChange 를 보고하지 않으면 상단 액션바("시나리오 구성으로")가 잠긴 채로 남는다.
//   → **계획범위와 기준 버전이 둘 다 정해진 뒤에만** 완료로 보고한다. 잠금이 공짜로 얻어진다.
//
// 왜 여기서 고르나: FP 는 MP 처럼 활성 계획 버전을 자동 유도할 수 없다 — 일반 FP 버전에는
// z_plan_version 행이 아예 없고(00-OVERVIEW §2·§4.1), 조회 대상 FP 테이블은 전부
// (plan_scope, version_cd) 키다. 스코프를 코드에 고정하면 리포 시드(DEFAULT)와 실 테넌트
// (AMKOR)가 갈린다.
//
// 단일/복수 분기는 <PlanScope> 가 이미 갖고 있다 — 선택지가 1개 이하면 렌더하지 않고
// top 옵션을 자동 선택해 onChange 로 통지한다. 목록도 FP 매핑 + use_yn 필터가 걸린
// getModulePlanScopes("FP") 이라, module 행이 없는 스코프(예: DOMESTIC)가 애초에 안 나온다
// (그런 스코프를 고르면 PlanScopeService 가 localhost:8079 로 조용히 폴백한다).
//   ⚠️ 선택기가 안 보이는 것과 값이 없는 것은 다르다 — 값은 이 컴포넌트가 소유한다.
// ── 현황 분석 — 공용 상수 ───────────────────────────────────────────────────

// 🔴 서버가 detected_issues 문장 **안에** 원문 코드를 박아 보낸다:
//      llm_client.build_detected_issues → f"{problem_tp_cd} / {problem_reason_tp_cd} {cnt}건"
//    그대로 그리면 [D-062] 가 닫은 "원문 코드 노출"이 다른 문으로 다시 열린다.
//    ⚠️ 해당 행을 **버리지 않고 치환**한다 — 필터는 서버 문구가 바뀌면 행이 조용히 사라지지만,
//       치환은 실패해도 원문이 남아 눈에 보인다(fail-loud, 조용한 누락 금지).
const FP_CODE_TOKEN_RE = /\b(?:FP_[A-Z][A-Z0-9_]*|PROBLEM_REASON_[A-Z0-9_]+)\b/g;

function fpTranslateCodes(s, t) {
  if (typeof s !== "string" || !s) return s;
  return s.replace(FP_CODE_TOKEN_RE, (m) => {
    // [D-113] 계획 실패·납기 지연 코드는 FP 현황 전용 라벨로 — 원인 분해 칩과 같은 이름(기존 코드 라벨은 zh 短交·vi 영어)
    const label = fpProblemTpLabel(t, m);
    return label && label !== m ? label : m;   // 번역 키가 없으면 원문 유지
  });
}

// severity 색·라벨(FP_SEVERITY)과 KPI 카드 footer 는 현황판 파일(fpStatusBoard.js)로 옮겼다 — [D-109].
//   ⚠️ ontime_rate/fulfill_rate/avg_used_rate 를 카드에 따로 그리지 않는 원칙은 그대로다: KPI b/a/c 와
//      같은 SQL 이라 한 화면에 같은 수치를 두 번 그리면 정의가 갈릴 때 어느 쪽이 참인지 알 수 없다.
//   🔵 e 카드의 「합계 {jc_time_total}」 footer 는 뺐다 — 자원 원장 합계(분)라 카드 값(e = 교체 활동 합,
//      공통 수요 기준)과 **정의가 다른 두 숫자**가 한 카드에 나란히 떴다(155: 24,360분 vs 합계 24840).

/**
 * 현황 분석 본문 — **순수 프리젠테이션**. 조회·선택은 FpExtraInitContent 가 한다.
 *
 * 🔴 분리한 이유: FpExtraInitContent 는 VersionSearchGroup → PlanScope(레거시 HOC) 를 전이 import 해
 *    redux·API 를 끌고 온다. 렌더 테스트에서 모의가 폭증하므로 그리는 부분만 떼어 단언한다.
 */
export function FpStatusContent({ data, planScope, versionCd }) {
  const { t } = useTranslation();

  // ⚠️ useMemo 로 감싼 이유는 성능이 아니라 **참조 안정성**이다 — `?? {}` 는 current_state 가
  //    없을 때 매 렌더 새 객체를 만들고, 그것이 아래 kpiCards useMemo 의 deps 에 들어가 있다.
  //    react-hooks/exhaustive-deps 경고가 나고, CI(`CI=true`)의 react-scripts 는 경고를 에러로
  //    승격하므로 `.github/workflows/saas-web.yml` 의 `npm run build` 가 그 한 건으로 깨진다.
  const state   = useMemo(() => data?.current_state ?? {}, [data]);
  // ⚠️ problem_evidence 는 최상위와 current_state 안에 **중복**으로 온다.
  //    최상위만 읽는다 — 서버가 최상위를 `or {}` 로 정규화하고(항상 dict), 타 모듈 화면도 전부
  //    flat 계약을 읽는다. 두 경로로 읽으면 한쪽만 바뀌었을 때 무엇이 그려졌는지 알 수 없다.
  const pe      = data?.problem_evidence ?? {};
  const reasons = Array.isArray(pe.reasons) ? pe.reasons : [];
  const peOff   = pe.problem_table_enabled === false;
  const orderCount = state.order_count;

  // 최근 실행 이력은 **미집계 빈 패널 전용**이다(scenarioStatusUi.js 의 useLastRunSummary 주석).
  //   타 5모듈은 전부 notAggregated / released_fact_available === false 를 넘기는데 FP 만 true 를
  //   하드코딩해, 현황이 채워진 화면에서도 매번 GET /history 를 쏘고 **결과를 버리고** 있었다
  //   (2026-09-18 ai_scenario_50 로그로 확인). FP 의 대응 조건은 아래 빈 패널 분기와 같은 orderCount === 0 이다.
  const lastRun = useLastRunSummary("FP", orderCount === 0);

  // 기준 버전 배지 — 부모가 주는 latestVersions["FP"] 는 쓰지 않는다.
  //   FP 일반 버전엔 z_plan_version 행이 없고([D-031]), 그 조회는 del_flg 필터가 없어
  //   과거 AI 격리 버전([D-029])을 "기준 버전"으로 보여 줄 수 있다 — 거짓 표시가 된다.
  //   releaseTs 도 넘기지 않는다: 배지가 그것을 "릴리즈일"로 렌더하는데 FP 에 그 개념이 없다.
  const versionInfo = useMemo(
    () => (versionCd ? { found: true, name: `${planScope} · ${versionCd}` } : { found: false }),
    [planScope, versionCd],
  );

  // ── [D-109] 비교 기준 — 서버 기본값(직전 계획 우선)에서 시작하고 사용자가 토글한다.
  //   다른 기준 계획으로 바뀌면(응답 교체) 서버 기본값으로 돌아간다 — 옛 선택이 새 응답에 없을 수 있다.
  const defaultKind = data?.kpi_compare?.default ?? null;
  const [cmpKind, setCmpKind] = useState(defaultKind);
  useEffect(() => { setCmpKind(defaultKind); }, [defaultKind, data?.version_cd]);
  const kind = data?.kpi_compare?.options?.[cmpKind] ? cmpKind : defaultKind;

  // KPI 카드 — 서버 kpi_keys 순서. 🔴 값 유무는 kpi_current(원시)로 가른다 · 0 은 실측값이다 ·
  //   미지 축은 버리지 않는다(buildFpStatusKpiCards 머리말). e·f 는 서버 label_key 가 같아 화면 라벨을 쓴다.
  //   🔵 색은 **서버 판정(kpi_tone)** 이다 — 종전에는 목표·밴드·전기 대비가 응답에 하나도 없어 색을
  //      칠하지 않았다([D-043] 계열). 이제 서버가 절대 밴드(a·b)와 비교 기준 대비 방향을 내려준다.
  const labelOf = useMemo(
    () => Object.fromEntries(FP_MODULE.kpiKeys.map((k, i) => [k, FP_MODULE.kpiLabels[i]])), []);
  const kpiCards = useMemo(() => fpKpiCardsForRender(buildFpStatusKpiCards(data, t, kind, labelOf)), [data, t, kind, labelOf]);   // [D-115] 단위 nowrap 노드(한자 단위가 글자 사이에서 쪼개졌다 — 캡처 195·196)
  // 열 수는 **현황 탭의 실제 폭**으로 고른다(KPI 5·3·2·1 · 인사이트 3·2·1 · TOP 2·1). 종전의 뷰포트 기준
  //   (min-width:1536px)은 사이드 메뉴·대화창이 연 폭을 몰라 650px 창에서도 카드 3열로 값을 잘랐다(캡처 172·173).
  const [areaRef, areaW] = useFpElementWidth();

  // 주요 이슈 — 서버 현황 신호(status_signals, [D-109]). 비교 기준마다 「악화」 신호가 달라서 선택을 따라간다.
  //   null = 구 서버(신호 필드 없음) → 종전 분기(detected_issues · 안내 키)로 떨어진다.
  const signals = useMemo(() => {
    if (!Array.isArray(data?.status_signals)) return null;
    const byKind = kind ? data?.status_signals_by_compare?.[kind] : null;
    return Array.isArray(byKind) ? byKind : data.status_signals;
  }, [data, kind]);

  const issues = useMemo(() => {
    const list = Array.isArray(data?.detected_issues) ? data.detected_issues : [];
    return list
      .map((it, i) => ({ ...it, _i: i }))
      .sort((a, b) => (FP_SEVERITY_ORDER[a.severity] ?? 9) - (FP_SEVERITY_ORDER[b.severity] ?? 9) || a._i - b._i);
  }, [data]);

  // 🔴 계획 결과가 0건이면 여기서 끝낸다. 이 분기가 없으면 치명적이다 —
  //    행이 0이면 short=0·late=0·util=None 이라 build_detected_issues 가 자동으로 [] 를 돌려주고,
  //    그러면 "감지된 이슈 없음"이라는 **안심 신호**가 데이터 없는 버전에 뜬다.
  if (orderCount === 0) {
    return (
      <StatusEmptyPanel
        title={t("statusNoAggregatedResultTitle")}
        desc={t("fpStatusNoResultDesc", { scope: planScope, version: versionCd })}
        summary={[
          { label: t("statusSelectedVersionLabel"), value: `${planScope} · ${versionCd}` },
          { label: t("statusLastRunLabel"), value: lastRun || KPI_EMPTY_DASH },
        ]}
      />
    );
  }

  const board = data?.status_board ?? null;
  // 문제 원인 분해 막대 — 스케일은 최다 사유, 비중은 **전체 작업주문 대비**다(사유끼리 합하면 100% 를 넘을 수
  //   있어 — 한 작업주문이 여러 사유에 잡힌다, fpStatusProblemCntTip — 사유 합계를 분모로 쓰지 않는다).
  const maxReason = reasons.reduce((m, r) => Math.max(m, Number(r.cnt) || 0), 0);

  return (
    <Stack ref={areaRef} spacing={1.5}>
      {/* ⓒ KPI — 카드 5장(보조 축은 카드 안 줄로 접는다) + 비교 기준 줄 + 납기·리드타임 구성 [D-109].
            제목·부제는 StatusKpiSection 이 고정한다(전 모듈 동일). */}
      <StatusKpiSection
        versionInfo={versionInfo}
        columns={fpKpiColumns(areaW)}
        cards={kpiCards}
        empty={data?.kpi_compare ? <FpCompareBar data={data} kind={kind} onKindChange={setCmpKind} /> : null}
        right={
          state.plan_start_ts && state.plan_end_ts ? (
            // 날짜는 쪼개지 않는다 — 좁은 창에서 공용 섹션 머리가 이 칸을 조이면 「2026- / 09-22 / ~」 처럼
            //   다섯 줄로 부서졌다(캡처 174). 이제 꺾여도 「기간 / 시작 ~ / 끝」 세 줄까지다.
            <Typography sx={{ fontSize: FONT.BODY, color: UIC.text3, textAlign: "right" }}>
              {t("period")}{" "}
              <Box component="span" sx={{ whiteSpace: "nowrap" }}>{String(state.plan_start_ts).slice(0, 10)} ~</Box>{" "}
              <Box component="span" sx={{ whiteSpace: "nowrap" }}>{String(state.plan_end_ts).slice(0, 10)}</Box>
            </Typography>
          ) : undefined
        }
      >
        <FpKpiBreakdown data={data} />
        <Typography sx={{ fontSize: FONT.BODY, color: UIC.text3 }}>
          {t("fpStatusLeadTimeDecompNote")}
        </Typography>
      </StatusKpiSection>

      {/* ⓕ 주요 이슈.
          🔵 [D-109] 서버 **현황 신호**(결과 테이블 + 고정 규칙 · i18n 키만)가 오면 그것을 그린다 — 종전에는
             이 칸이 늘 「AI 분석을 생성하지 못했습니다」였다(LLM 실패가 아니라 이 경로에 재료가 없던 설계).
             신호가 **빈 배열**이면 규칙이 돌았는데 걸린 것이 없다는 뜻이라 「이슈 없음」이 맞다.
          ⚠️ 신호 필드가 **없으면**(구 서버) 종전 계약 그대로다 — 2026-09-22 upstream 변경 이후 이 경로의
             detected_issues 는 빈 목록 + 안내 키라, 「이슈 없음」(안심 신호)과 「만들지 못했음」을 갈라 말한다. */}
      <StatusSection
        title={t("mainIssueTitle")}
        subtitle={signals && issues.length === 0
          ? t("fpStatusIssueSourceNote", { title: t("fpStatusNarrativeTitle") }) : undefined}
        right={signals && issues.length === 0 && signals.length > 0 ? (
          <Typography sx={{ fontSize: FONT.BODY, color: UIC.text3 }}>
            {t("fpStatusIssueCount", { count: signals.length })}
          </Typography>
        ) : undefined}
      >
        {issues.length === 0 && signals && signals.length > 0 ? (
          <FpSignalCards signals={signals} columns={fpCardColumns(areaW)} />
        ) : issues.length === 0 ? (
          <StatusItemList>
            <StatusItemRow
              accent={UIC.g1}
              bar={false}
              name={
                <Typography sx={{ fontSize: FONT.BODY, fontWeight: 600, color: UIC.text1 }}>
                  {/* 신호 모드(빈 배열)에서는 안내 키를 쓰지 않는다 — 규칙은 돌았고 걸린 것이 없다. */}
                  {(signals ? "" : tKeyOr(t, data?.detected_issues_key, "")) || t("fpStatusSignalNoneDesc")}
                </Typography>
              }
            />
          </StatusItemList>
        ) : (
          <Box sx={fpCardGridSx(fpCardColumns(areaW))}>
            {issues.map((it) => {
              const sev = FP_SEVERITY[it.severity] ?? FP_SEVERITY.LOW;
              return (
                <StatusInsightCard
                  key={`${it.severity}-${it._i}`}
                  accent={{ color: sev.color, text: sev.text }}
                  eyebrow={t(sev.labelKey)}
                  title={fpTranslateCodes(it.issue, t)}
                >
                  {it.detail && (
                    <Typography sx={{ fontSize: FONT.BODY, color: UIC.text2, lineHeight: 1.5 }}>
                      {fpTranslateCodes(it.detail, t)}
                    </Typography>
                  )}
                </StatusInsightCard>
              );
            })}
          </Box>
        )}
      </StatusSection>

      {/* ⓓ 문제 항목 TOP 5 · ⓔ 기간별 자원 부하 추이 — [D-109]. 구 서버(현황판 없음)면 그리지 않는다. */}
      {board && <FpTopProblems board={board} twoCol={areaW ? areaW >= 1100 : undefined} />}
      {board && <FpLoadTrend board={board} />}

      {/* ⓖ 문제 원인 분해 — 엔진이 기록한 사유라 **표시 전용**이다([D-082]·[D-083] — 자원 귀속이 믿을 수
            없어 병목 판정에는 쓰지 않는다). 그 사실을 부제로 밝힌다. */}
      <StatusSection
        title={t("fpStatusProblemEvidenceTitle")}
        subtitle={board ? t("fpStatusProblemEvidenceSub") : undefined}
        right={peOff ? <StatusPendingTag /> : undefined}
        collapseDisabled
      >
        <Stack spacing={1.2}>
          {/* 🔴 "저장이 꺼져 판정 불가"를 "문제 없음"으로 읽히지 않게 세 겹으로 막는다:
                 헤더 중립 태그 + amber 안내 + 초록/정상 표기 금지. 행이 남아 있으면 함께 그린다
                 (사실을 숨기는 것도 거짓 표시다). */}
          {peOff && <StatusNotice icon={<ReportProblemOutlinedIcon sx={{ fontSize: 16 }} />}>
            {t("fpStatusProblemSaveOffNotice")}
          </StatusNotice>}

          {reasons.length === 0 ? (
            peOff
              ? <StatusEmpty variant="rows" count={3} />
              : (
                <StatusItemList>
                  <StatusItemRow
                    accent={UIC.g1}
                    bar={false}
                    name={
                      <Typography sx={{ fontSize: FONT.BODY, fontWeight: 600, color: UIC.text1 }}>
                        {t("fpStatusProblemNoneDesc")}
                      </Typography>
                    }
                  />
                </StatusItemList>
              )
          ) : (
            <>
              <StatusItemList>
                {/* 🔵 헤더 셀은 `label` 을 그린다(`node` 는 행 전용) — 종전에는 node 로 넘겨 칸 제목이 비어 있었고,
                       건수 칸이 { num, unit } 2열인데 헤더에 unit 이 없어 열 수가 어긋났다(캡처 155). */}
                {/* 🔵 건수가 겹칠 수 있다는 안내(fpStatusProblemCntTip)는 이름 칸 제목에 단다 — 종전에는 표 위에
                       「문제 상세 · 건」 캡션을 한 줄 더 두어 바로 아래 헤더와 같은 말을 두 번 했다(캡처 169).
                       건수 칸 제목은 단위(「cases」 소문자)가 아니라 열 이름이다(캡처 171). */}
                <StatusItemHeader
                  nameLabel={
                    <Tooltip title={t("fpStatusProblemCntTip")} placement="top-start">
                      <Box component="span" sx={{ cursor: "help" }}>{t("fpStatusColProblemDetail")}</Box>
                    </Tooltip>
                  }
                  rightW={0}
                  cells={[{ w: 90, label: t("fpStatusColProblemType"), align: "left" },
                          { w: 70, label: t("case"), unit: "" },
                          ...(orderCount > 0 ? [{ w: 52, label: t("fpSigMetricShare"), unit: "%" }] : [])]}
                />
                <Stack spacing={0.7}>
                  {reasons.map((r, i) => (
                    <StatusItemRow
                      key={`${r.problem_tp_cd}-${r.problem_reason_tp_cd}-${i}`}
                      accent={UIC.g3}
                      bar={{ value: Number(r.cnt) || 0, max: maxReason || 1,
                             color: r.problem_tp_cd === "FP_DELIVERY_SHORT" ? UIC.g3 : UIC.g2s }}
                      name={
                        <Typography sx={{ fontSize: FONT.BODY, fontWeight: 600, color: UIC.text1,
                                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {fpProblemTpLabel(t, r.problem_reason_tp_cd)}
                        </Typography>
                      }
                      // 유형은 TOP 목록 품목 행과 같은 칩 — 맨 글자는 본문 기본 크기(16px)로 떠 숫자보다 컸다(캡처 169).
                      cells={[
                        { w: 90, node: fpChip(fpProblemTpLabel(t, r.problem_tp_cd),
                                              r.problem_tp_cd === "FP_DELIVERY_SHORT" ? UIC.g3 : UIC.g2), align: "left" },
                        { w: 70, num: fpFmt(r.cnt, 0), unit: t("caseUnitLabel") },
                        ...(orderCount > 0
                          ? [{ w: 52, num: fpFix((Number(r.cnt) / orderCount) * 100, 1), unit: "%" }] : []),
                      ]}
                    />
                  ))}
                </Stack>
              </StatusItemList>
            </>
          )}
        </Stack>
      </StatusSection>

      {/* ⓗ AI 현황 요약 — gen_summary 2단계로 나중에 도착한다.
          🔴 요약이 **없는 것**과 **만들지 못한 것**은 다르다. 아직 안 온 동안에는 섹션을 그리지
             않지만, 서버가 `status_narrative: null` + `status_narrative_key` 로 실패를 알려 오면
             섹션이 **아무 말 없이 사라지는 대신** 그 안내를 낸다(upstream 계약, 2026-09-22). */}
      {(data?.status_narrative?.summary || data?.status_narrative_key) && (
        <StatusSection title={t("fpStatusNarrativeTitle")}>
          <Stack spacing={1}>
            <Typography sx={{ fontSize: FONT.SUB, color: UIC.text1, lineHeight: 1.6, whiteSpace: "pre-line" }}>
              {data?.status_narrative?.summary
                ? fpTranslateCodes(data.status_narrative.summary, t)
                : tKeyOr(t, data.status_narrative_key, "")}
            </Typography>
            {/* 🔴 옵셔널 체이닝이 필수다 — 요약 생성 실패 시 status_narrative 는 **null** 이고
                   위 분기는 status_narrative_key 만으로도 이 섹션을 그린다. */}
            {Array.isArray(data.status_narrative?.recommended_actions)
              && data.status_narrative.recommended_actions.length > 0 && (
              <StatusItemList>
                {data.status_narrative.recommended_actions.map((a, i) => (
                  <StatusItemRow
                    key={`${a.param_key}-${i}`}
                    accent={UIC.acc}
                    bar={false}
                    // 사유는 이름 뒤에 **줄바꿈**으로 잇는다 — 공용 `sub` 는 한 줄 말줄임이라 영문에서 근거 문장의
                    //   끝이 잘렸다(캡처 171). 권장 조치의 근거는 잘리면 뜻이 없다.
                    name={
                      <Typography sx={{ fontSize: FONT.BODY, color: UIC.text3, lineHeight: 1.5 }}>
                        <Box component="span" sx={{ fontWeight: 600, color: UIC.text1, mr: 0.8 }}>
                          {t(a.label_key || a.param_key)}
                        </Box>
                        {fpTranslateCodes(a.reason, t)}
                      </Typography>
                    }
                  />
                ))}
              </StatusItemList>
            )}
          </Stack>
        </StatusSection>
      )}
    </Stack>
  );
}

// ── 현황 분석 탭 — 기준 계획 선택 + 조회 ────────────────────────────────────

const FP_EMPTY_SEL = {
  planScope: "", planningDate: null, versionCd: "", plantCd: "", selectedVersion: null,
};
const fpSelKey = (scope, ver) => (scope && ver ? `${scope}:${ver}` : "");
const fpEchoKey = (o) => (o && o.plan_scope && o.version_cd ? `${o.plan_scope}:${o.version_cd}` : "");
/**
 * 요약 언어 표식(에이전트 코드 `vi`·`zh-TW` — 서비스가 새긴다)이 화면 언어(i18n 코드 `vi-VN`·`zh-TW`)에 해당하는가.
 * 매핑 정본은 `utils/i18n.toAgentLanguage` 다 — 여기서는 접두 일치만 본다(그 모듈의 단위 테스트 모의는 기본
 * 내보내기만 흉내 내므로 이름 있는 내보내기를 끌어오지 않는다).
 */
export const fpLangMatches = (stamp, uiLang) =>
  !!stamp && !!uiLang && String(uiLang).toLowerCase().startsWith(String(stamp).toLowerCase());

function FpExtraInitContent({ cachedData, onDataChange, onReadyChange }) {
  const { t } = useTranslation();

  // 🔴 탭 왕복이 사용자의 기준 버전을 조용히 갈아치우는 것을 막는 시드.
  //    ExtraInitContent 는 `curStep === 0 &&` 조건부 렌더라 탭을 옮길 때마다 **언마운트**된다.
  //    재마운트 시 sel 이 비면 VersionSearchGroup 이 versions[0] 으로 되돌려 버려서,
  //    사용자가 3번째 버전을 골라 놓고 구성 탭에 갔다 오면 기준 계획이 말없이 바뀐다.
  //    _planScope/_sourceVersionCd 와 같은 격(모듈 레벨·렌더 유발 없음·언마운트 생존)이다.
  const [sel, setSel] = useState(() => FP_MODULE._sel ?? FP_EMPTY_SEL);

  // 🔴 "쌍이 정착했는가" 게이트. planScope 를 A→B 로 바꿔도 VersionSearchGroup 은 versionCd 를
  //    즉시 비우지 않는다(2단계 effect 가 끝나야 갱신) ⇒ {B, A의 버전} 이라는 **유령 쌍**이 실재한다.
  //    서버는 이 조합을 거절하지 않는다 — COUNT(*) 기반이라 없는 조합에도 **0 으로 채운 200** 을
  //    돌려주므로 사용자는 완전히 렌더된 **거짓 현황**을 본다.
  //    ⚠️ 에코 대조(아래)로는 못 막는다: 서버가 (B, A버전) 을 그대로 에코해 주기 때문이다.
  //       두 장치가 담당하는 실패 계열이 서로 겹치지 않는다.
  const [pairSettled, setPairSettled] = useState(
    () => !!(FP_MODULE._sel?.planScope && FP_MODULE._sel?.versionCd),
  );

  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [err, setErr] = useState(null);

  const hasPair = !!sel.planScope && !!sel.versionCd;
  const selKey  = pairSettled ? fpSelKey(sel.planScope, sel.versionCd) : "";

  // 캐시는 **선택 키가 일치할 때만** 유효하다. 타 모듈의 `if (cachedData) return;` 를 그대로 쓰면
  // 다른 기준 계획의 현황이 영구히 눌러앉는다. 첫 렌더에서 동기 판정해야 상단 버튼이 깜빡이지 않는다.
  const [data, setData] = useState(() => {
    const k = fpSelKey(FP_MODULE._sel?.planScope, FP_MODULE._sel?.versionCd);
    return k && fpEchoKey(cachedData) === k ? cachedData : null;
  });

  const seqRef = useRef(0);
  useEffect(() => () => { seqRef.current += 1; }, []);   // 언마운트도 같은 장치로 취소

  const lang = i18n.language;

  const fetchState = useCallback((key, scope, ver) => {
    const seq = ++seqRef.current;
    setLoading(true); setErr(null);
    return scenarioService.getFpInitialState({ planScope: scope, versionCd: ver })
      .then((r) => {
        if (seq !== seqRef.current) return;                      // 뒤늦은 응답
        if (fpEchoKey(r) !== key) {                              // 에코 불일치 = 받지 않는다
          setErr(t("fpStatusBaselineMismatch")); return;
        }
        setData((prev) => (prev?.status_narrative && fpEchoKey(prev) === key
          ? { ...r, status_narrative: prev.status_narrative }     // 요약이 먼저 왔으면 보존
          : r));
      })
      .catch((e) => {
        if (seq !== seqRef.current) return;
        setErr(e?.response?.data?.detail || e.message || t("fpStatusQueryFailed"));
      })
      .finally(() => { if (seq === seqRef.current) setLoading(false); });
  }, [t]);

  // LLM 요약은 느리다(Bedrock 동기 호출) — 기본 데이터와 **따로** 받아 도착하면 보강한다.
  // ⚠️ MP 는 서버 TTL 캐시에 적중시켜 기본 쿼리 재실행을 피하지만 FP 서버에는 그 캐시가 없어
  //    기본 SQL 이 두 번 돈다. FP 기본 조회는 순수 DB(ms 급)라 수용한다 — 서버 dedup 은 별건이다.
  const fetchNarrative = useCallback((key, scope, ver) => {
    const seq = seqRef.current;
    setAiLoading(true);
    scenarioService.getFpInitialState({ planScope: scope, versionCd: ver, genSummary: true })
      .then((r) => {
        if (seq !== seqRef.current) return;
        if (fpEchoKey(r) !== key) return;
        // 🔴 실패 안내 키도 옮긴다 — 기본 응답이 먼저 와 있으면 prev 가 이기므로, 키를 빠뜨리면 요약이 실패했을 때
        //    「생성하지 못했습니다」 안내 없이 섹션이 조용히 사라졌다(키는 요약 응답에만 있다).
        setData((prev) => ({ ...(prev ?? r), status_narrative: r.status_narrative,
                             status_narrative_key: r.status_narrative_key, _lang: r._lang }));
      })
      .catch(() => { /* 실패해도 기본 데이터는 유지 */ })
      .finally(() => { if (seq === seqRef.current) setAiLoading(false); });
  }, []);

  useEffect(() => {
    seqRef.current += 1;                       // 진행 중 요청 전부 무효화
    FP_MODULE._sel = hasPair ? sel : null;     // 시드(선택이 완성됐을 때만)
    // 정착 전에는 null 로 유지한다 — 유령 쌍이 analyze/챗 페이로드로 새는 것이
    // fetchTemplates 가 throw 하는 것보다 훨씬 비싼 실패다.
    FP_MODULE._planScope = selKey ? sel.planScope : null;
    FP_MODULE._sourceVersionCd = selKey ? sel.versionCd : null;

    if (!selKey) { setData(null); setErr(null); setLoading(false); return; }
    if (fpEchoKey(data) === selKey) {
      // 이미 보유 — 언어가 바뀌었으면 요약만 다시 받는다.
      //   🔴 표식은 에이전트 코드(`vi`)이고 lang 은 i18n 코드(`vi-VN`)다 — 그대로 비교하면 늘 달라서 현황 탭에
      //      돌아올 때마다 요약(LLM 호출 수십 초)을 다시 만들었다.
      //   🔴 다른 언어로 만든 요약은 새 요약이 올 때까지 **내린다** — 그대로 두면 베트남어 화면에 한국어 요약이
      //      떠 있었다(캡처 191). 섹션 대신 아래 「요약 생성 중」 줄이 선다.
      const staleLang = !!data.status_narrative && !fpLangMatches(data._lang, lang);
      if (staleLang) setData((prev) => (prev ? { ...prev, status_narrative: undefined } : prev));
      if (!data.status_narrative || staleLang) fetchNarrative(selKey, sel.planScope, sel.versionCd);
      return;
    }
    setData(null);                              // 🔴 새 버전 배지 아래 옛 KPI 가 남지 않게 먼저 비운다
    fetchState(selKey, sel.planScope, sel.versionCd)
      .finally(() => fetchNarrative(selKey, sel.planScope, sel.versionCd));
  }, [selKey, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  // 부모 캐시에 올린다. 🔴 실패는 올리지 않는다 — initialState != null 이 되면 컨테이너의
  //    statusBusy 폴백이 "준비됨"으로 읽고 다음 마운트에서 조회를 건너뛴다(fetchTemplates 독과 같은 형태).
  //    null 은 올린다 = 선제 무효화(선택이 바뀐 창에서 소비처가 옛 기준 계획을 가리키지 않게).
  const lastPubRef = useRef(undefined);
  useEffect(() => {
    if (err) return;
    if (data === lastPubRef.current) return;
    lastPubRef.current = data; FP_MODULE._planPeriod = fpPlanPeriodOf(data);   // [D-120] 비조업 위젯의 계획 기간 밖 경고 — _planScope 와 같은 격
    onDataChange?.(data);
  }, [data, err]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🔴 "선택됨" → "선택됨 AND 그 선택의 조회가 끝남". 타 모듈 식(!loading && (data||err))을
  //    그대로 쓰면 선택 전에는 조회가 없어 영원히 false 다. 실패도 true 로 보고한다(사용자가 갇히지 않게).
  //    ⚠️ LLM 요약(aiLoading)은 넣지 않는다 — 넣으면 느린 요약 때문에 탭 이동이 수십 초 잠긴다.
  const ready = hasPair && !loading && (!!data || !!err);
  useEffect(() => { onReadyChange?.(ready); }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSelChange = useCallback((patch) => {
    // 정착 신호는 patch 의 **모양**에서 온다(VersionSearchGroup 은 onReady 류 prop 이 없다):
    //   PlanScope → {planScope} · 1단계 → {planningDate} · 2단계/직접선택 → {versionCd, ...}
    // ⚠️ versionCd 검사를 마지막에 두어 한 patch 에 둘이 함께 와도 정착으로 본다.
    if ("planScope" in patch || "planningDate" in patch) setPairSettled(false);
    if ("versionCd" in patch) setPairSettled(true);   // 값이 "" 여도 '정착'이다
    setSel((prev) => ({ ...prev, ...patch }));
  }, []);

  return (
    <Stack spacing={1.5}>
      <VersionSearchGroup value={sel} onChange={onSelChange} />

      {!hasPair && (
        <Card variant="outlined" sx={{ borderRadius: 2, border: `1px solid ${C.border}` }}>
          <CardContent sx={{ p: "14px 16px !important" }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <InfoOutlinedIcon sx={{ fontSize: 18, color: C.primary, mt: "1px" }} />
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">{t("fpScnBaseSelectTitle")}</Typography>
                <Typography variant="caption" sx={{ color: C.text2, whiteSpace: "pre-line" }}>
                  {t("fpScnBaseSelectBody")}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {hasPair && loading && (
        <Card variant="outlined" sx={{ borderRadius: 2, border: `1px solid ${UIC.border}` }}>
          <LinearProgress sx={{ height: 2 }} />
          <CardContent sx={{ p: "18px 16px !important" }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} sx={{ color: UIC.acc }} />
              <Typography sx={{ fontSize: FONT.BODY, color: UIC.text2 }}>
                {t("statusLoadingSituationData")}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      )}

      {hasPair && !loading && err && (
        <Card variant="outlined" sx={{ borderRadius: 2, border: `1px solid ${UIC.g3}30` }}>
          <CardContent sx={{ p: "12px 14px !important" }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ReportProblemOutlinedIcon sx={{ fontSize: 18, color: UIC.g3 }} />
              <Typography sx={{ flex: 1, minWidth: 0, fontSize: FONT.BODY, color: UIC.g3t }}>{err}</Typography>
              <IconButton size="small" aria-label={t("fpStatusRetry")}
                // 요약도 다시 부른다 — fetchState 가 seq 를 올려 진행 중이던 요약 응답을 버리므로, 여기서 다시 부르지
                //   않으면 「요약 생성 중」 줄이 끝나지 않는다(effect 경로와 같은 순서).
                onClick={() => fetchState(selKey, sel.planScope, sel.versionCd)
                  .finally(() => fetchNarrative(selKey, sel.planScope, sel.versionCd))}>
                <RefreshRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
          </CardContent>
        </Card>
      )}

      {hasPair && !loading && !err && data && (
        <>
          <FpStatusContent data={data} planScope={sel.planScope} versionCd={sel.versionCd} />
          {aiLoading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 0.5 }}>
              <CircularProgress size={12} sx={{ color: UIC.acc }} />
              <Typography sx={{ fontSize: FONT.BODY, color: UIC.text3 }}>
                {t("fpStatusNarrativeLoading")}
              </Typography>
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
}

// ── FP 모듈 설정 객체 ───────────────────────────────────────────────────────
// 값의 문자열은 i18n 키 — 모듈 레벨 상수라 t()를 직접 쓸 수 없으므로 소비처(공용 컨테이너)가
// t(key)로 변환한다. 예외: templates[].scenarios[].name/desc 는 사용자 편집 필드 초기값이라 원문.
export const FP_MODULE = {
  code: "FP",
  label: "menuAiFpScenario",
  step4lbl: "fpConfirmLabel",
  //   flowTopNodes = 공통 3(현황·시나리오 확정·KPI 해석) + 분기 앵커 1.
  engineTrack: { parentTool: "fp_step4_engine", childPrefix: "fp_engine_",
                 match: "role", flowTopNodes: 4 },
  skipKpiSection: true,

  // 엔진 대기 총시간 상한 — FP 만 135분 [Q-108]
  //   🔴 실측이 **엔진만 64분 35초**였다(2026-09-21, requestId=3863f446, 성공 3/3). 공용 기본값
  //      65분에 여유가 **25초**였고, 엔진 종료가 끝도 아니다 — 그 뒤 KPI 집계 SQL 과 Bedrock
  //      해석이 남고 z_ai_scenario_request.status_cd 는 파이프라인 **맨 끝**에서만 바뀐다
  //      ⇒ 폴러가 정상적으로 붙었어도 그 런은 시간 안에 결과를 못 받았다.
  //   🔵 공용 상수(ENGINE_POLL_DEADLINE_MS 65분)를 올리지 않고 **FP 만** 덮는다 — 올리면
  //      BF·DP·IP·RP·MP 의 행 탐지까지 함께 늦어진다([D-044] 가 같은 이유로 FP 전용 노브를 만들었다).
  //   ⚠️ 안쪽 사다리와 함께 움직여야 한다: FastAPI SCENARIO_FP_ENGINE_TIMEOUT_SEC 7200초(120분)
  //      < 이 값(135분) <= Java engine-stream-timeout-ms / 엔진 progress-ttl-min(150분).
  //      이 값만 올리면 안쪽(120분)이 새 절단선이 되어 화면에 엔진 오류로 뜬다.
  enginePollDeadlineMs: 135 * 60 * 1000,

  // SSE 워치독 옵트인 [Q-107] — 무이벤트 3분이면 스트림을 버리고 폴러로 강등한다.
  //   🟢 FP 는 제네릭 라우트(`scenario_generic.py`)를 타서 **10초 하트비트**가 온다.
  //      2026-09-22 캡처로 브라우저 도달까지 확인했다(간격 10,001~10,044ms).
  //      그래서 "무이벤트 3분"이 죽은 스트림의 정확한 신호가 된다 — 실제 tool 이벤트 간격은
  //      5분 51초까지 벌어지므로, 핑을 세지 않으면 이 값으로는 건강한 런을 죽인다.
  //   🔴 **다른 모듈이 이 키를 베껴 가지 말 것** — IP·RP 는 하트비트가 없는 자기 analyze/stream
  //      라우트를 탄다(scenario_ip.py·scenario_rp.py). 근거는 engineSseWatchdog.js 머리말.
  sseIdleTimeoutMs: SSE_IDLE_TIMEOUT_MS,

  // Tab2/3(비교·확정) 표시용 기준 시나리오 셰이프 폴백 — 엔진 결과(agentResults)가 채워지기 전
  // KPI 비교/확정 화면의 골격으로 쓰인다. scenarios 를 정의하면 kpiLabels/kpiKeys 는 동반 필수
  // (컨테이너가 옵셔널 체이닝 없이 접근 — 누락 시 화면 전체 크래시).
  scenarios: [
    {
      role: "base", name: "scnFpBaseName", desc: "scnFpBaseDesc",
      tags: ["scnTagKeepCurrent"],
      score: 0, kpis: { a: "-", b: "-", c: "-", g: "-", i: "-", d: "-", e: "-", f: "-", h: "-" },
    },
    {
      role: "alt1", name: "scnFpAlt1Name", desc: "scnFpAlt1Desc",
      tags: ["scnFpTagOnTime"],
      score: 0, kpis: { a: "-", b: "-", c: "-", g: "-", i: "-", d: "-", e: "-", f: "-", h: "-" },
    },
    {
      role: "alt2", name: "scnFpAlt2Name", desc: "scnFpAlt2Desc",
      tags: ["scnFpTagUtilization"],
      score: 0, kpis: { a: "-", b: "-", c: "-", g: "-", i: "-", d: "-", e: "-", f: "-", h: "-" },
    },
  ],

  // KPI 8축 — 정본은 **서버 db_reader.FP_KPI_META** 다([D-051] 로 화면 승계에서 이관).
  //   a~f 는 SimulationCompare(KPI_DEFS) 승계 그대로이고(D-009), g·i 가 [D-051] 추가분이다.
  // 🔴 아래 세 배열은 **인덱스로 짝지어진다** — 길이·순서가 서버 FP_KPI_META 삽입 순서와
  //    같아야 한다. 어긋나면 그 행이 조용히 "—" 가 되고, kpiLabels 자체가 없으면
  //    AiScenarioPlanning.js 의 mod.kpiLabels.map 이 옵셔널 체이닝 없이 돌아 흰 화면이 된다.
  // [D-114] FP 전용 KPI 라벨 키 — 기존 키(FP_PLAN_FULFILLMENT_RATE 등)는 zh-CN 이 번체, vi 가 영어라 새 키로 그린다.
  kpiLabels: [
    "fpKpiFulfillRate",           // a 계획 충족률
    "fpKpiPlanOntimeRate",        // b 계획 준수율
    "fpKpiAvgUtil",               // c 평균 자원 가동률
    "fpKpiInstageDwell",          // g 공정 내 체류
    "fpKpiInterstageWait",        // i 공정 간 대기
    "fpKpiLeadTime",              // d 평균 생산 리드타임 (= g + i, 표시 전용·점수 가중치 0)
    "fpKpiJobChangeTime",         // e 작업 교체 시간
    "fpKpiJcCountLabel",          // f 작업 교체 횟수
    "fpKpiExtraCapacity",         // h 추가 투입 능력 ([D-073]·[D-074] 대가 축)
  ],
  kpiKeys: ["a", "b", "c", "g", "i", "d", "e", "f", "h"],
  // ⚠️ g·i 를 d 보다 **앞**에 둔 이유: 추천 히어로 Δ 요약이 방향축 중 앞 4개만
  //    보여준다(AiScenarioPlanning.js deltaRows .slice(0, 4)). d 가 앞서면 i 가
  //    잘려 "리드타임이 왜 줄었는가"의 절반이 안 보인다. 서버 FP_KPI_META 도 같은 순서다.
  // c(가동률)는 old 화면 관례상 높을수록 좋게 강조하지 않는다(과부하와 표리) → neutral.
  // g·i 는 짧을수록 좋다 — d 와 같은 방향이다.
  // h(추가 투입 능력)는 **맨 끝**이다 — 앞에 두면 위 slice(0,4) 에서 i 가 잘린다.
  //   능력을 더 쓸수록 나쁘므로 min. base 는 정의상 0 이다([D-074]).
  kpiDir: { a: "max", b: "max", c: "neutral",
            g: "min", i: "min", d: "min", e: "min", f: "min", h: "min" },

  // 🔴 [Q-111] — `c`(평균 자원 가동률)는 **neutral 축**인데 비교표가 ▲/▼ 배지를 그린다.
  //    실측: 효율 카드는 27.2 → 24.3(▼), 증설 카드는 27.2 → 27.9(▲) — **같은 축이 상황에 따라
  //    반대 방향으로 「좋음」처럼 읽힌다**. 🟢 색은 이미 중립이고 bestMap 승자 하이라이트에서도
  //    이미 빠져 있다(공용 `dir === "neutral"` 분기) — 남은 것이 **화살표 글리프 하나**다.
  //  ⇒ `noDelta` 로 그 배지만 끈다. **공용 파일 0줄.**
  //  ⚠️ Δ 수치까지 함께 사라진다 — km.cellExtra/renderValue 는 인자가 (sc, t) 뿐이라 base 값에
  //     접근할 수 없어 "중립색 Δ"를 FP 단독으로는 못 그린다. 수치를 남기려면 공용 1줄이 필요하다.
  //  🔵 배열 인덱스는 **kpiKeys 순서와 1:1**이다(a b c g i d e f h) — `c` 가 index 2.
  //     다른 소비처는 StepKpiAnalysis 뿐인데 FP 는 skipKpiSection:true 라 먼저 null 을 낸다.
  kpiMeta: [{}, {}, { noDelta: true }, {}, {}, {}, {}, {}, {}],

  // ── 챗 퀵프롬프트 — 단계별 "지금 물어볼 만한 것" ────────────────────────
  // 🔴 **답할 수 있는 질문만 넣는다.** 아래는 전부 MODULE_FP_TOOLS 로 실제 답이 되는
  //    것만 골랐다(fp_sql_tools.py) — 눌렀는데 못 답하는 버튼은 없느니만 못하다.
  // ⚠️ **칩은 단계당 앞 2개만 뜬다** — AgentChatPanel.js:326 이 slice(0, 2) 한다.
  //    그래서 개수보다 **순서**가 중요하다. 뒤쪽은 슬래시 팔레트·자유입력에서만 쓰인다.
  quickPrompts: {
    analysis: {
      // get_fp_kpis · get_fp_problem_evidence · get_fp_scope_targets
      0: [
        { i18nKey: "fpQuickPromptKpi", ko: "현황 KPI 해석해줘" },
        { i18nKey: "fpQuickPromptLateCause", ko: "납기 지연이 왜 났어?" },
        { i18nKey: "fpQuickPromptBottleneck", ko: "어느 자원이 병목이야?" },
        { i18nKey: "fpQuickPromptWhatIs", ko: "FP AI 시나리오로 무엇을 할 수 있나요?" },
      ],
      // get_fp_param_baseline · 위키(표준 시나리오·정책 설명)
      // ⚠️ fpQuickPromptPolicyHelp 는 "정책 4가지" 라고 쓰는데 fp_chat_rules.md 는
      //    "3종" 이다. 언어팩 기존 값은 고치지 않는 것이 규약이라 **뒤로 물렸다** —
      //    칩으로는 안 뜨고 팔레트에만 남는다. 정정 판단은 [Q-133].
      1: [
        { i18nKey: "fpQuickPromptBaseline", ko: "지금 계획 설정이 뭐야?" },
        { i18nKey: "fpQuickPromptPresetDiff", ko: "표준 시나리오 3종의 차이를 설명해줘" },
        { i18nKey: "fpQuickPromptPolicyHelp", ko: "계획 정책 4가지는 각각 무엇인가요?" },
      ],
      // compare_fp_scenario_kpi · compare_fp_scenario_facts([Q-129])
      2: [
        { i18nKey: "fpQuickPromptBestScenario", ko: "어떤 시나리오가 최적이야?" },
        { i18nKey: "fpQuickPromptItemDiff", ko: "어느 품목이 나빠졌어?" },
      ],
      3: [
        { i18nKey: "fpQuickPromptConfirmCheck", ko: "확정 전 체크리스트" },
      ],
    },
  },

  // ── Action 탭: 화면 하위상태(step·mode)에 맞는 "지금 가능한 액션"만 선언 ──────
  //   🔴 이 속성 하나가 **두 스코프를 동시에 연다** — module:FP(AiScenarioPlanning.js:6546)
  //      와, 공용이 이미 써 둔 aiRec:FP(:1448). 후자의 게이트가
  //      `typeof mod.agentActions !== "function"` 이라, 여기가 비어 있는 동안 FP 는
  //      AI 추천 생성/재생성/적용을 **챗으로 시킬 수 없었다**(화면 버튼은 멀쩡했다).
  //   description/confirmKo 는 모듈 레벨(컴포넌트 아님)이라 useTranslation 훅을 못 쓴다
  //      → i18n 싱글턴으로 미리 번역한다(BF/DP/IP/MP/RP agentActions 와 동일 패턴).
  agentActions: ({ step, mode, aiResult, exec }) => {
    const selMode = (m, key, ko) => {
      const modeLabel = i18n.t(key, { defaultValue: ko });
      return {
        id: `fp.mode.${m}`, i18nKey: key, ko, group: "action", style: "secondary",
        description: i18n.t("ipSelectModeDesc", { mode: modeLabel }),
        run: () => exec({ action: "select_mode", payload: { mode: m } }),
        confirmKo: i18n.t("ipSelectModeConfirm", { mode: modeLabel }),
      };
    };
    // 🔴 FP 만 **자기 확인 문구**를 쓴다 — 엔진이 실측 1시간 이상인데(위 engineTrack 주석)
    //    공용 MSG_IpEngineRunConfirm 은 "진행 상황을 확인하세요" 뿐이라, 챗으로 띄운
    //    사용자는 멈춘 줄 안다. 시간을 말하는 것이 이 자리에서 유일하게 빈 것이다.
    // ⚠️ `enabled: () => !!mode` 는 **일부러 걸지 않았다.** step1Mode 기본값이 null 이고
    //    (AiScenarioPlanning.js:6543) 그 상태가 곧 표준/직접 경로다 — 막으면 가장 흔한
    //    경로에서 버튼이 죽는다. 컨테이너가 null→"custom" 으로 접는 것(:5982)은
    //    5모듈 공통의 정상 동작이지 사고가 아니다.
    const runEngine = {
      id: "fp.engine.run", i18nKey: "engineRunLabel", ko: "엔진 실행",
      group: "action", style: "primary", description: i18n.t("fpRunEngineDesc"),
      run: () => exec({ action: "run_engine" }),
      confirmKo: i18n.t("MSG_FpEngineRunConfirm"),
    };
    if (step === 0) return [
      selMode("ai",     "agentActFpModeAi",     "AI 추천 시나리오 생성"),
      selMode("custom", "agentActFpModeCustom", "표준 시나리오 선택"),
    ];
    if (step === 1) {
      // AI 방식인데 아직 추천 결과가 없으면 여기서는 아무것도 주지 않는다 —
      //   그 구간은 aiRec:FP 스코프(ai.generate)가 담당한다.
      if (mode === "ai" && !aiResult) return [];
      return [runEngine];
    }
    return [];   // 단계 이동은 컨테이너 공통 nav.step* 이 전담한다
  },

  // 🔵 applyChatParam 은 두지 않는다 — 공용 폴백 두 분기가 FP 의 저장 형태와 **둘 다**
  //    맞는다. 표준·AI 카드는 fpFlattenVariant 가 overrides 키를 지워 flat params
  //    분기(AiScenarioPlanning.js executeAction set_param 끝의 mod.params 경로)로 가고, "직접 입력"
  //    카드는 그 앞의 overrides 분기로 간다. 유일한 불일치인 "챗이 IP 셰이프를 심는다"는
  //    fpFlattenVariant 가 [Q-069] 근거로 이미 전역 지정으로 접고 있다. MP 가 이 훅을 만든 이유
  //    (수요 우선순위 랭킹 병합)는 FP 카탈로그에 대응 개념이 0건이다. [D-095]
  //    ⚠️ 훅이 있어도 못 가로채는 분기가 셋 있다 — external_factors · `payload.op` 목록형 ·
  //    mf_qty_formula 가 훅보다 먼저 return 한다. FP 챗은 op·목록형을 만들지 못하고(서버 FP 스키마에
  //    list 0개 — test_fp_chat_tools.py), 다른 화면에서 보관된 값이 여기로 새던 교차 화면 경로는
  //    공용 챗 엔진의 보관 주인 대조가 막는다(useAgentChatEngine.js · [D-108]).
};

// FP_MODULE에 현황 탭 안내 카드 등록
FP_MODULE.ExtraInitContent = FpExtraInitContent;

// ── 구성 탭(step 1) 상단 슬롯 — 「무엇을 바꿀지, 왜」 ─────────────────────────
//   공용 컨테이너가 StickyActionBar 직하에 렌더한다(AiScenarioPlanning.js:7427·:7433).
//   표준·직접·AI 어느 방식을 골라도 한 번 그려지고, **엔진 실행 중·AI 구성 중에는 통째로
//   사라진다**(그 구간엔 진행률이 주인공이다).
//
// 🔴 이 자리에 대상·파라미터·KPI 를 그리지 않는다 — 전부 이미 다른 곳에 있다.
//    대상    = FpScenarioTargets(카드 안, F-17)  ⚠️ scope_targets 는 상류에서 20건 선절단이라
//              여기서 「N개 중 M개」로 쓰면 **거짓 표시**가 된다.
//    파라미터 = 공용 ScenarioParamDefsFields(카드 안)
//    KPI     = 현황 탭 StatusKpiSection. FP 는 skipKpiSection 으로 중복 KPI 를 한 번 잘라낸 전례가 있다.
//    충족률·준수율·가동률도 넣지 않는다 — KPI a/b/c 와 같은 SQL 이라 한 화면에 두 번 그리면
//    정의가 갈린다(위 current_state 주석과 같은 이유).
//  ⇒ **여기서만 의미가 생기는 것은 「왜 이 레버인가」 하나다.** 현황 탭이 1줄 목록으로
//    흘려보낸 recommended_actions 를, 사용자가 그 레버를 실제로 만지는 자리로 옮긴다
//    (MP 가 같은 데이터를 구성 탭에서 카드로 승격시킨 것과 같은 패턴).
//
// 🟢 **새 조회가 없다.** cache 는 현황 탭이 올린 initialState 가 그대로 내려온 것이다
//    (컨테이너 :7441 의 2단 폴백 `composeExtra ?? initialState`). MP 는 status_narrative 가
//    없을 수 있어 재조회하지만 FP 는 **현황 탭에서 계획범위·버전을 고르지 않으면 구성 탭에
//    못 온다** — 없으면 재조회 대신 null 을 낸다. 공용 래퍼의 `&:empty` 가 여백까지 접는다(:7436).
function FpComposeRationale({ cache }) {
  const { t } = useTranslation();
  // 🔴 다른 언어로 만든 사유는 그리지 않는다 — 이 탭에 있는 동안 현황 탭(FpExtraInitContent)은 언마운트라 언어가
  //    바뀌어도 요약을 다시 받지 않는다. 레버 이름(t(label_key))만 새 언어가 되고 사유 문장은 옛 언어로 남았다
  //    (캡처 191 과 같은 증상). 결정형 문제 사유 줄은 언어와 무관하니 남긴다. 표식이 없는 캐시(구 응답)는 종전대로.
  const staleLang = !!cache?._lang && !fpLangMatches(cache._lang, i18n.language);
  const actions = !staleLang && Array.isArray(cache?.status_narrative?.recommended_actions)
    ? cache.status_narrative.recommended_actions : [];

  const ev = cache?.problem_evidence || {};
  const reasons = Array.isArray(ev.reasons) ? ev.reasons : [];
  const peOff = ev.problem_table_enabled === false;
  // 상위 3건만 — 이 자리는 근거 요약이고 전체 표는 현황 탭이 갖는다.
  const top = [...reasons].sort((a, b) => (b?.cnt ?? 0) - (a?.cnt ?? 0)).slice(0, 3);

  // 🔴 **권장 레버(LLM)와 문제 사유(결정형) 중 하나만 있어도 그린다.**
  //    처음엔 recommended_actions 에만 걸었는데 그건 **가장 잘 비는 필드**다 —
  //    LLM 이 실패하면 서버가 `{summary:"", recommended_actions:[]}` 를 돌려주고
  //    (`fp/llm_client.py:271`) 실측에서 실제로 그 상태를 봤다(캡처 ai_scenario_112 의
  //    "AI 분석을 생성하지 못했습니다"). 그때도 `problem_evidence` 는 살아 있었다
  //    — 근거가 있는데 근거 패널이 사라지는 것이 사용자에게 가장 나쁘다.
  //    둘 다 없을 때만 null 이다(공용 래퍼의 `&:empty` 가 여백까지 접는다).
  if (!actions.length && !top.length && !peOff) return null;

  return (
    <StatusSection
      title={t("fpComposeRationaleTitle")}
      subtitle={t("fpComposeRationaleSubtitle")}
      right={peOff ? <StatusPendingTag /> : undefined}
      collapsible
    >
      <Stack spacing={1.2}>
        {/* 권장 레버가 없으면 격자 자체를 만들지 않는다 — 빈 grid 는 gap 만 남아
            "무언가 로딩 중"으로 읽힌다. 그때는 아래 문제 사유 줄만 남는다. */}
        {actions.length > 0 && (
        <Box sx={STATUS_CARD_GRID_SX}>
          {/* 레버 이름은 현황 탭과 **같은 방식으로** 해석한다(t(label_key || param_key)) —
              갈리면 두 탭이 같은 것을 다른 이름으로 부른다. param_key 는 내부 코드라
              번역이 없을 때만 노출되는 폴백이다. */}
          {actions.map((a, i) => (
            <StatusInsightCard
              key={`${a.param_key}-${i}`}
              accent={{ color: UIC.acc, text: UIC.accT }}
              eyebrow={t("fpComposeRationaleEyebrow")}
              title={t(a.label_key || a.param_key)}
            >
              {a.reason && (
                <Typography sx={{ fontSize: FONT.BODY, color: UIC.text2, lineHeight: 1.5 }}>
                  {fpTranslateCodes(a.reason, t)}
                </Typography>
              )}
            </StatusInsightCard>
          ))}
        </Box>
        )}

        {/* 🔴 저장 옵션이 꺼져 있으면 "문제 없음"으로 읽히지 않게 막는다 — 현황 탭과 같은 처리.
               건수를 안 보여주는 것이 아니라 **판정 불가임을 먼저 말한다**. */}
        {peOff ? (
          <StatusNotice icon={<ReportProblemOutlinedIcon sx={{ fontSize: 16 }} />}>
            {t("fpStatusProblemSaveOffNotice")}
          </StatusNotice>
        ) : top.length > 0 && (
          // 🔴 서버는 (유형 × 상세) 조합마다 한 행이다 — 상세만 그리면 「Capacity 부족」 이 유형별로 두 번 나와
          //    중복으로 읽힌다(계획 실패 1,632 · 납기 지연 297 — 캡처 ai_scenario_127). 현황 탭이 「문제 유형」 열을
          //    따로 두는 것과 같은 정보를, 서버 build_detected_issues 와 같은 「유형 / 상세」 형식으로 싣는다.
          //    건수는 합치지 않는다 — 한 WO 가 여러 조합에 잡힐 수 있다. 유형이 없는 구응답은 상세만. [Q-140]
          <Typography sx={{ fontSize: FONT.SUB, color: UIC.text3, lineHeight: 1.6 }}>
            {top.some((r) => r.problem_tp_cd)
              ? `${t("fpStatusColProblemType")} / ${t("fpStatusColProblemDetail")}`
              : t("fpStatusColProblemDetail")}
            {top.map((r, i) => (
              <Box component="span" key={`${r.problem_tp_cd}-${r.problem_reason_tp_cd}-${i}`}>
                {"\u00A0\u00B7 "}
                {r.problem_tp_cd && (
                  <Box component="span" sx={{ color: UIC.text2 }}>{`${fpProblemTpLabel(t, r.problem_tp_cd)} / `}</Box>
                )}
                <Box component="span" sx={{ color: UIC.text2 }}>{fpProblemTpLabel(t, r.problem_reason_tp_cd)}</Box>
                {` ${Number(r.cnt ?? 0).toLocaleString()}${t("caseUnitLabel")}`}
              </Box>
            ))}
          </Typography>
        )}
      </Stack>
    </StatusSection>
  );
}

// FP_MODULE에 구성 탭 근거 패널 등록
FP_MODULE.ComposeExtraContent = FpComposeRationale;

// ── 실현 가능성 경고 (비교 탭) — 백엔드 결정형 판정(scn.feasibility_warnings)을 공용 위젯이 렌더.
//   FP 는 R-FP-04(가상 설비 + 셋업 레버)·R-FP-05(무한 능력 자원 + 효율). 둘 다 레버가 서로를
//   조용히 무효화하는 계열이라, 실행은 되는데 결과 해석이 무너지는 자리를 짚는다.
//   ⚠️ 규칙·문구는 전부 서버 소유다(saas-ai-agent/src/scenario/fp/feasibility_rules.py) —
//      여기서는 공용 위젯에 슬롯만 연결한다. 표시 문자열은 i18n 키로만 온다.
FP_MODULE.feasibilityCfg = { sectionTitleKey: "feasWarnSectionTitle" };
FP_MODULE.scenarioWarnCount = feasWarnCount;   // KPI 비교표 '경고' 행(공용 슬롯, tone 만 전달)
FP_MODULE.RecHeroBadges = FeasibilityHeroBadges;

// ── 표준 시나리오 카드(정적) — 데모 프리셋 3종 ──────────────────────────────
// 정본: saas-fp/docs/fp-ai-scenario/docs/04-REF-PARAM-SURFACE.md §2.6 (D-014 v1 레버만 사용).
// 최상위 id/industry/name/useCase/desc/tags 는 i18n 키, scenarios[].name/desc 는 편집 초기값(원문).
// 각 변형에 param_defs 를 임베드해 공용 ScenarioParamDefsFields 가 편집기를 그린다.
// overrides 는 넣지 않는다 — FP 스코프 축이 공용 SCOPE_DEF 에 없어(E2E 때 F-17 판정)
// 전역 파라미터 직접 편집 폴백이 데모에 올바른 경로다.
FP_MODULE.templates = [
  {
    id: "fp_otd_focus",
    industry: "recommendedTagLabel",
    name: "fpTplOtdFocusName",
    useCase: "fpTplOtdFocusUseCasePlanOntime",   // [D-113] KPI b 이름 = 카드 「계획 준수율」(HEAD 키 값은 그대로)
    desc: "fpTplOtdFocusDesc",
    tags: ["standardTagLabel", "scnFpTagOnTime"],
    scenarios: [
      { role: "base", name: "현행 유지", desc: "현재 정책 그대로 수립한 기준 계획.",
        params: {}, param_defs: FP_PARAM_DEFS },
      { role: "alt1", name: "납기 우선 정책", desc: "Backward 전개 + 납기·주문 우선순위 정렬로 지연을 최소화.",
        params: {
          plan_strategy: "BACKWARD",
          order_seq_rule_1: "RULE_PLAN_SEQ_DUE_DATE",
          order_seq_rule_2: "RULE_PLAN_SEQ_ORDER_PRIORITY",
          production_rule_1: "RULE_SCHEDULE_END_DATE",
          stock_use_rule_1: "RULE_STOCK_DATE",
        },
        param_defs: FP_PARAM_DEFS },
    ],
  },
  {
    id: "fp_util_focus",
    industry: "standardTagLabel",
    name: "fpTplUtilFocusName",
    useCase: "fpTplUtilFocusUseCase",
    desc: "fpTplUtilFocusDesc",
    tags: ["standardTagLabel", "scnFpTagUtilization"],
    scenarios: [
      { role: "base", name: "현행 유지", desc: "현재 정책 그대로 수립한 기준 계획.",
        params: {}, param_defs: FP_PARAM_DEFS },
      { role: "alt1", name: "생산성 우선 정책", desc: "Forward 전개 + BOR 우선순위·빠른 종료 선호, 자원 효율 1.15x.",
        params: {
          plan_strategy: "FORWARD",
          order_seq_rule_1: "RULE_PLAN_SEQ_ORDER_PRIORITY",
          production_rule_1: "RULE_PRIORITY",
          efficiency: 1.15,
        },
        param_defs: FP_PARAM_DEFS },
    ],
  },
  // 🔴 fp_overtime(잔업 확대) 카드는 2026-09-15 에 **제거했다** — 설명이 *"능력 증설"* 인데
  //    applier 가 비가동(D) 구간만 넣어 실제로는 **축소**였다(Q-020). 시드에서도 뺐다.
  //    i18n 키(fpTplOvertime*)는 append-only 규약대로 남겨 둔다.
];

// ── F-18: DB 표준 시나리오 → 카드 격자 ──────────────────────────────────────
// 공용 StepScenarioCreate 가 mod.fetchTemplates 존재 시 이 결과를 템플릿 카드로 렌더한다.
// 백엔드 계약: GET /scenario/fp/scenarios → {scenarios:[{scenario_cd, scenario_name(_key),
//   scenario_direction(_key), desc_txt(_key), source_type, param_defs[], variants[]}],
//   scope_targets, problem_targets}
//   (정본: saas-ai-agent/src/scenario/fp/param_catalog.py fetch_scenario_catalog)
// ⚠️ 백엔드는 `variants`, 공용 컨테이너는 `scenarios` 다 — 리네임이 빠지면 조용히 빈 카드가 된다.
//
// 🔴 이 매퍼는 **전역 전용(global-only)** 변환이다 [F-17 1차 권장 (a)]. 공용
//    ScenarioParamDefsFields 는 FP 의 per-param override 스키마
//    ({param_key, scope_type, targets, value})를 읽지 못하고 `overrides[0].params[key]` 를
//    본다(scenarioParamDefsFields.js:560,573). 그대로 넘기면 시드 값이 빈칸으로 보이는데
//    엔진에는 그대로 가는 "보는 것 ≠ 도는 것" 이 된다 → flat params 로 접고 overrides 를 지운다.

/** 공용 편집기가 실제로 렌더하는 param_def type (scenarioParamDefsFields.js:124-158).
 *  ordered_multi 는 슬롯 select 로 펼치고 toggle 은 select 로 바꾼다(아래). period_list(resource_downtime)는
 *  FP 위젯(fpDowntimeFields)이 그리고, 설계상 제외 레버는 서버 `excluded` 로 거른다 [D-120].
 *  나머지(multi/code_list/matrix/datetime)는 맨 텍스트 입력으로 떨어져 규칙 코드를 손으로 치게 되므로 노출하지 않는다. */
const FP_RENDERABLE_TYPES = ["select", "number", "multiplier"];

/** ordered_multi(순서 있는 규칙 3칸) 슬롯 라벨 — canonical 키 → 슬롯별 i18n 키.
 *  공용 편집기에 순서 입력 위젯이 없어(Q-015) "N순위 select 행"으로 펼친다.
 *  슬롯 키 자체는 백엔드와 **같은 규칙**으로 만든다(복수형 끝 s 제거 + _N) —
 *  param_mapper._SLOT_BASE = {key[:-1]: key} 이고 _fold_slot_keys 가 번호순 배열로 되접는다. */
const FP_SLOT_LABEL_KEYS = {
  order_seq_rules:  ["fpScnParamOrderSeqRule1", "fpScnParamOrderSeqRule2", "fpScnParamOrderSeqRule3"],
  production_rules: ["fpScnParamProductionRule1", "fpScnParamProductionRule2", "fpScnParamProductionRule3"],
  stock_use_rules:  ["fpScnParamStockUseRule1", "fpScnParamStockUseRule2", "fpScnParamStockUseRule3"],
};

/** FP 불리언 레버(material_infinite·lazy_job_change)를 select 로 그린다.
 *  공용 toggle 분기는 IP 재고 문구가 하드코딩(ipTargetInvSafetyShort/CycleShort, :145-146)이고
 *  현재값을 appliedPctLabel(백분율)로 찍으며(:408) "Y" 문자열만 켜짐으로 읽는다(:144) —
 *  FP 시드 값은 JSON true 라 그대로 두면 **켰는데 꺼진 것으로 보인다**.
 *  on/off 는 6개 언어팩에 이미 있는 공용 키다(신규 키 0). */
const FP_BOOL_OPTIONS = [
  { value: "N", label_key: "off" },
  { value: "Y", label_key: "on" },
];

/** canonical 복수형 → 슬롯 키. ("order_seq_rules", 0) → "order_seq_rule_1" */
const fpSlotKey = (canonical, i) =>
  `${canonical.endsWith("s") ? canonical.slice(0, -1) : canonical}_${i + 1}`;

/** param_def 1건 → 공용 편집기가 그릴 수 있는 def 0~N개 */
function fpDefsForSharedEditor(def) {
  if (!def?.param_key || def.excluded) return [];   // [D-120] 설계상 제외(서버 excluded) — wo_* 빈 드롭다운 등
  if (def.type === "ordered_multi") {
    const labels = FP_SLOT_LABEL_KEYS[def.param_key];
    if (!labels) return [];                     // 라벨 없는 신규 ordered_multi 는 노출하지 않는다(키 원문 노출 방지)
    const slots = Math.min(def.max_slots || labels.length, labels.length);
    const cur = Array.isArray(def.current_value) ? def.current_value : [];
    return Array.from({ length: slots }, (_, i) => ({
      ...def,
      param_key:       fpSlotKey(def.param_key, i),
      label:           undefined,               // label_key 만 남긴다(t() 대상)
      label_key:       labels[i],
      type:            "select",
      options:         def.options || [],       // {value,label,label_key} — label_key = RULE_* 코드(그 자체가 i18n 키)
      current_value:   cur[i] ?? null,          // 배열 현재값을 슬롯별 스칼라로 — 남기면 원문 코드가 찍힌다
      current_display: null,                    // 서버가 만든 한글 폴백 — 화면에 내보내지 않는다 [D-028]
      max_slots:       undefined,
    }));
  }
  if (def.type === "toggle") {
    // 현재값도 옵션 값(Y/N)으로 바꾼다 — boolean 그대로 두면 공용 캡션의 옵션 매칭(String(false) ≠ "N")이
    //   빗나가 `String(cv)` 폴백으로 「현행 설정: false」 가 찍힌다(캡처 ai_scenario_127). [Q-066] ⑷
    const cv = def.current_value;
    return [{ ...def, type: "select", options: FP_BOOL_OPTIONS, current_display: null,
              current_value: typeof cv === "boolean" ? (cv ? "Y" : "N") : cv }];
  }
  if (!FP_RENDERABLE_TYPES.includes(def.type)) return fpDowntimeDefForEditor(def);   // [D-120] period_list 만 FP 위젯으로 · 나머지는 []
  return [def];
}

/**
 * variant 1건 → 공용 편집기의 legacy direct-params 분기가 읽는 형태.
 *
 * scope_type !== "all" 인 override 는 전역으로 접으면 안 되는 것들이다 —
 *    order_priority/order_due_shift/order_qty_rate 는 카탈로그 scopes 가 [order, loc_item],
 *    lazy_job_change 는 [route] 로 `all` 이 아예 허용되지 않는다.
 *    (이력) [D-059] 는 여기서 그 값을 버렸다 — 당시 param_mapper._apply 가 scopes 를 검증하지 않아
 *    전사 일괄로 적용됐기 때문이다. [D-063] 부터는 값은 params 로, 스코프는 곁길(fp_scopes)로
 *    보존해 전송하고, param_mapper 가 허용되지 않은 스코프를 fail-closed 로 무시한다(아래 [Q-061] 주석).
 */
function fpFlattenVariant(v, inject) {
  const params = {};
  const put = (key, value) => {
    if (value === null || value === undefined || value === "") return; if (FP_DOWNTIME_KEYS.has(key)) return fpDowntimePut(params, key, value);   // [D-120] 구간 목록은 그대로
    if (Array.isArray(value)) {
      const labels = FP_SLOT_LABEL_KEYS[key];
      if (!labels) return;                      // 슬롯으로 표현 못 하는 배열은 버린다
      value.slice(0, labels.length).forEach((item, i) => {
        if (item !== null && item !== undefined && item !== "") params[fpSlotKey(key, i)] = item;
      });
      return;                                   // 🔴 canonical 복수형은 넣지 않는다 — param_catalog.py (merge_folded_slots) 의
    }                                           //    flat.setdefault 에서 복수형이 이겨 슬롯 편집이 버려진다
    params[key] = typeof value === "boolean" ? (value ? "Y" : "N") : value;
  };

  Object.entries(v?.params || {}).forEach(([k, val]) => put(k, val));

  // 🔴 세부 스코프(order/route/…)는 **버리지 않고 곁길에 보존한다** [Q-061].
  //   예전에는 여기서 drop 해서 `SCN-FP-004`·`005` 가 엔진에 아무것도 못 보냈다 —
  //   화면은 「조정 0」인데 9분 돌린 KPI 가 base 와 글자까지 같았다(ai_scenario_37 실증).
  //   ⇒ 값은 params 로(편집·표시·저장 공용), 스코프는 fp_scopes 로, 합치는 것은
  //      전송 직전 FP_MODULE.buildEngineScenario 가 한다.
  //   overrides 키 자체는 아래에서 계속 지운다 — Array.isArray(overrides) 가 공용에서
  //      8곳을 가르기 때문이다(스코프 편집기 전환·되돌리기·조정 집계·챗).
  const scopes = {};
  let droppedArr = 0;
  (v?.overrides || []).forEach((ov) => {
    // 🔴 챗 set_param 은 **IP 셰이프**({scope_type, loc_cd, item_cd, params:{…}})를 심는다
    //    (AiScenarioPlanning.js executeAction set_param 의 overrides 분기) — per-param 셰이프와 달리 `param_key` 가 없다.
    //    그냥 return 하면 아래에서 overrides 키가 통째로 지워지므로 **사용자가 챗으로 바꾼 값이
    //    조용히 증발한다**(예전에는 편집기가 overrides[].params 를 읽어 보여 주고 있었다).
    //    FP 화면에는 거점·품목군 축이 없으므로(09-REF §7) 그 값은 **전역 지정으로 접는 것**이
    //    맞고, 그래야 편집기가 보여 주던 값과 같아진다. [Q-069]
    if (!ov?.param_key) {
      Object.entries(ov?.params || {}).forEach(([k, val]) => put(k, val));
      return;
    }
    const scopeType = ov.scope_type || "all";
    if (scopeType !== "all") {
      // ⚠️ 배열 값은 슬롯 키로 쪼개져 params 에 들어가므로 canonical 키로 되돌릴 수 없다 — 비조업 구간([D-120])만
      //    목록 그대로 둔다. 그 밖의 배열 레버에 세부 스코프가 걸리면 알린다(ordered_multi 3종은 전부 scopes=[all]).
      if (Array.isArray(ov.value) && !FP_DOWNTIME_KEYS.has(ov.param_key)) { droppedArr += 1; return; }
      // 🔵 [D-101] 서버가 자동 선정한 근거(`target_selection`)를 곁길에 함께 둔다 — 표시 전용이다.
      //    엔진으로는 나가지 않는다: buildEngineScenario 가 override 를 4키로 **새로** 만든다.
      scopes[ov.param_key] = { scope_type: scopeType, targets: ov.targets || [],
                               ...(ov.target_selection ? { selection: ov.target_selection } : {}) };
    }
    put(ov.param_key, ov.value);
  });
  if (droppedArr) {
    console.warn(`[FP] ${v?.scenario_cd || "?"}/${v?.role || "?"}: 배열 레버에 세부 스코프가 걸려 ${droppedArr}건 전송 제외 — 슬롯 키로 왕복 불가`);
  }

  // enabled/overrides 는 벗겨낸다.
  //   enabled  — FP 는 overrides 유무로 계산하므로 접은 뒤엔 의미가 없는데, 그대로 두면
  //              챗 컨텍스트·에이전트 필터가 카드를 "비활성"으로 본다.
  //   overrides — 🔴 반드시 키 자체를 지운다. 빈 배열도 truthy 라 makeAddedScenario:341 이
  //              deep-copy 하고, 편집기가 Array.isArray(overrides) 로 모드를 갈라
  //              스코프 편집기(FP 미지원 축)로 뒤집힌다.
  const { enabled, overrides, ...rest } = v || {};
  return {
    ...rest,
    params,
    param_defs: (v?.param_defs || []).flatMap(fpDefsForSharedEditor),
    ...(Object.keys(scopes).length ? { fp_scopes: scopes } : {}),
    ...inject,
  };
}

/** 엔진이 이해하는 스코프 — 정본은 Java 다.
 *  FpAiScenarioParamApplier 의 resolveDemands/resolveResources/resolveBors 가 받는 값이고,
 *  그 밖은 WARN + 빈 리스트로 **조용히 skip** 된다(:798 자원 · :878 BOR · :942 오더).
 *  ⚠️ 카탈로그의 `loc_item` 은 여기 없다 — Java 가 모른다. `item` 으로 번역하면 안 된다:
 *     Java SCOPE_ITEM 은 품목 코드로 매칭하는데 loc_item 대상은 "품목@플랜트" 결합 코드라
 *     **조용히 0건 매칭**이 된다(무증상 실패를 다른 층으로 옮길 뿐). */
const FP_ENGINE_SCOPES = new Set(["all", "resource", "route", "bor", "order", "item"]);

// ── 엔진 전송 payload 조립 (공용 화면이 mod.buildEngineScenario 로 위임 호출) [Q-061] ──
// fpFlattenVariant 가 곁길(fp_scopes)에 남긴 스코프를 읽어 per-param override 로 되돌린다.
//   계약 3가지:
//   ⑴ 값의 정본은 **항상 sc.params** — 사용자가 카드에서 고친 값이 그대로 나가고, 비우면 override 가 사라진다.
//   ⑵ params 에서 **반드시 뺀다.** 안 빼면 param_mapper._apply 가 scope=all 엔트리와
//      scope=order 엔트리를 **둘 다** 만들어 전 주문에 적용된 뒤 대상만 덮어쓴다([D-059] 가 두려워한 그림).
//   ⑶ POLICY 슬롯 키(order_seq_rule_1…)는 손대지 않는다 — 서버 merge_folded_slots 경로 유지.
// 평탄화 산물이 아니면(fp_scopes 없음) null 을 돌려 공용 기존 식에 맡긴다.
FP_MODULE.buildEngineScenario = (sc) => {
  const scopes = sc?.fp_scopes;
  if (!scopes || !Object.keys(scopes).length) return null;

  const params = { ...(sc.params || {}) };
  const overrides = Array.isArray(sc.overrides) ? [...sc.overrides] : [];
  const tag = `${sc?.scenario_cd || "?"}/${sc?.role || "?"}`;

  Object.entries(scopes).forEach(([key, meta]) => {
    const value = FP_DOWNTIME_KEYS.has(key) ? fpDowntimeEngineValue(params[key]) : params[key];   // [D-120] 완성 행만 · 없으면 ""
    delete params[key];                                   // ⑵ — 어느 경로로 가든 전역 전송은 막는다
    if (value === null || value === undefined || value === "") return;   // 사용자가 지웠다

    const scopeType = meta?.scope_type || "all";
    const targets = Array.isArray(meta?.targets) ? meta.targets : [];
    if (!FP_ENGINE_SCOPES.has(scopeType)) {
      console.warn(`[FP] ${tag}: 엔진이 모르는 스코프 '${scopeType}' — ${key} 전송 제외`);
      return;
    }
    if (!targets.length) {
      console.warn(`[FP] ${tag}: ${key} 의 대상이 비어 엔진에서 무효 — 전송 제외`);
      return;
    }
    // 🔴 4키로 **명시해** 새로 만든다 — 곁길의 표시 전용 필드(selection·user_edited [D-101])가
    //    구조적으로 엔진에 닿지 않는 이유가 이 줄이다. `...meta` 로 바꾸면 안 된다.
    overrides.push({ param_key: key, scope_type: scopeType, targets, value });
  });

  // 🔴 잔여 위험 가시화 — 카탈로그가 `all` 을 허용하지 않는 레버(16종)가 flat params 에 남아
  //   있으면 서버 param_mapper 가 fail-closed 로 드롭한다. 사용자에겐 "값을 넣었는데 아무 일도
  //   안 일어남" 으로 보이므로 최소한 콘솔에 남긴다 — 이 세션이 계속 잡아 온 "보는 것 ≠ 도는 것" 이다.
  //   ⚠️ 근본 해소는 F-17(대상 선택 UI)이다. 여기서 값을 지우지는 않는다 — 판정은 서버가 한다.
  (sc.param_defs || []).forEach((d) => {
    const sp = d?.scopes;
    if (!Array.isArray(sp) || !sp.length || sp.includes("all")) return;
    const v = params[d.param_key];
    if (v === undefined || v === null || v === "") return;
    console.warn(`[FP] ${tag}: ${d.param_key} 는 대상 지정이 필요한 레버다(scopes=${sp.join(",")}) — 전역값은 엔진이 받지 않는다`);
  });

  return { params, overrides };
};

// ── 미리보기 비교표 값 포맷 (공용 화면이 mod.formatOverrideValue 로 위임 호출) [Q-062] ──
// FP 정책 레버는 값이 **배열**(order_seq_rules 등 ordered_multi)인데, 공용 fmtVal 은
//   배열을 String(raw) 한 덩어리로 옵션 매칭한다(AiScenarioPlanning.js:535-537) —
//   그래서 **원소가 1개일 때만 우연히 한글**이 되고 2개 이상은 `RULE_A,RULE_B` 원문 코드가 찍힌다.
//   1원소는 공용 경로가 이미 맞게 그리므로 **여기 도달하는 것은 N≥2 뿐**이다(옵션 매칭이 먼저다).
//   좌(현행)·우(제안) 두 열이 같은 fmtVal 을 지나므로 한 번에 고쳐진다.
//   ⚠️ 매칭 안 되는 원소는 **버리지 않고 원문을 남긴다** — 조용한 누락이 원문 노출보다 나쁘다.
//   ④ **boolean 스칼라**도 여기서 받는다 — AI 추천 미리보기는 정규화 전 원본 def(`type:"toggle"`·
//      `options:null`)를 넘겨 옵션 매칭을 건너뛰므로, 안 받으면 `String(raw)` 폴백이 「false → true」 를 찍는다.
//      on/off 는 FP_BOOL_OPTIONS 와 같은 공용 키다(6팩 존재). [Q-062] ④
//   배열도 boolean 도 아니면 null 을 돌려 공용 제너릭 포맷(multiplier/unit/String)에 맡긴다.
FP_MODULE.formatOverrideValue = (raw, def) => {
  if (typeof raw === "boolean") return i18n.t(raw ? "on" : "off"); if (fpIsDowntimeValue(raw, def)) return fpFormatDowntime(raw);   // [D-120] 「MM-DD ~ MM-DD」
  if (!Array.isArray(raw)) return null;
  const parts = raw
    .filter(v => v !== null && v !== undefined && v !== "")
    .map(v => {
      const o = (def?.options || []).find(x => String(x.value) === String(v));
      return o ? i18n.t(o.label_key || o.label) : String(v);
    });
  return parts.length ? parts.join(", ") : null;
};

// ── AI 추천(suggest) 안내 문구 [Q-031] ──────────────────────────────────────
//   없으면 공용 컨테이너가 **DP 용 기본값**으로 폴백해 화면에 "수요 실적·BF MAPE·
//   과조정 이력" 같은 FP 와 무관한 문구가 뜬다(AiScenarioPlanning.js 의 aiRec* 폴백).
//   ⚠️ aiRec(정적 추천 인덱스)은 **일부러 넣지 않는다** — mod.scenarios(정적 데모 3종)의
//      인덱스인데 FP 는 fetchTemplates 로 그 배열을 대체하므로 가리킬 대상이 없다.
FP_MODULE.aiRecCardDesc    = "fpAiRecCardDesc";
FP_MODULE.aiRecIntroLines  = ["fpAiRecIntroLine1PlanOntime", "fpAiRecIntroLine2"];   // [D-113] 「계획 준수율」
//   분석 인자 칩 — **점수를 움직이는 네 축**이다([D-058] 로 c·e·f 는 가중치 0).
//   기존 KPI 라벨 키를 그대로 재사용해 신규 i18n 을 늘리지 않는다.
FP_MODULE.aiRecFactors     = ["fpKpiFulfillRate", "fpKpiPlanOntimeRate",          // [D-114] KPI 라벨과 같은 키
                             "fpKpiInstageDwell", "fpKpiInterstageWait"];
FP_MODULE.aiRecLoadingDesc = "fpAiRecLoadingDescPlanOntime";   // [D-113] 「계획 준수율」
FP_MODULE.aiRecButtonLabel = "agentActFpModeAi";

// AI 추천(suggest) 결과를 템플릿 경로와 **같은 셰이프**로 접는다 [Q-031].
//   두 경로가 갈리면 같은 카드가 경로에 따라 다른 계획을 돈다. 정본은 **평탄화**다 —
//   세부 스코프는 fpFlattenVariant 가 fp_scopes 곁길로 보존하고 전송 직전 buildEngineScenario 가
//   합친다([D-063] — 서버 param_mapper 도 허용되지 않은 스코프를 fail-closed 로 무시한다).
//   ⚠️ inject 는 비운다 — scope_targets/problem_targets 는 추천 응답이 이미 자기 값을 갖고 있고,
//      primary_param_keys 는 카드(scn) 레벨이라 variant 단위인 여기서는 알 수 없다.
FP_MODULE.normalizeSuggested = (v) => fpFlattenVariant(v, {});

// ── 파라미터 편집기 — 공용 편집기를 **FP 정규화 입력**으로 재사용한다 [Q-069] ───────────────
//  🔴 왜 스위치를 켜나: 공용 addBlank 가 「직접 입력」 카드에 `overrides: []` 를 박는데
//     (AiScenarioPlanning.js:1537-1538, 챗 액션 :5855·:5883·:5964 도 동형), 편집기가
//     Array.isArray(overrides) 로 모드를 갈라(scenarioParamDefsFields.js:330) **빈 배열도 배열**이라
//     평탄화 분기를 통과하지 못하고 스코프 분기로 간다. 거기서 FP 는 isIp 가 거짓이라
//     **RP 축(거점/품목군/전체)** 으로 폴백한다(:383). 거점·품목군은 FP 축이 아니고
//     (09-REF §7 = all·resource·workcenter·route·bor·loc_item·order), 후보도 problem_targets 에서
//     오는데 FP 는 그 값을 하위호환 빈 배열로 고정한다(param_catalog.py) ⇒ 고르면 **영구 0건**이다.
//  🟢 공용 파일은 한 줄도 고치지 않는다 — mod.handlesParamDefs 스위치가 **이미 있고**
//     (AiScenarioPlanning.js:898) RP·MP·DP·BF 가 같은 방식으로 자기 위젯을 준다. [D-010] 승인 0건.
//  🔵 주입 지점은 4곳인데 **편집기는 1곳**이라 여기서 가로채면 트리거가 늘어도 자동으로 덮인다.
// 🔵 [D-084] B — 공용 요약기(**흐름도 노드 부제** · 카드 hover 상세)는 flat params 카드의
//    대상을 못 본다. 평탄화가 스코프를 `fp_scopes` **곁길**로 옮겨 두고, 공용 쪽은 평탄 params 를
//    전부 `scopeType:"all"` 로 접기 때문이다(scenarioParamUtil).
//    ⇒ 공용 요약기가 **detail 이 빌 때만** 부르는 폴백 훅을 준다(scenarioParamSummary).
//       훅을 안 준 5모듈(BF·DP·IP·RP·MP)에는 **바이트 동일 no-op** 이다.
//  🟢 2026-09-23 (F-17) — 공용 SCOPE_LABEL_KEY 에 `bor`·`order` 2행을 더했다.
//     대상 선택기가 그 두 축을 열었으므로 "오늘은 실해가 없다"는 전제가 끝났다.
//     두 라벨 키(FP_BOR·mpOrderLabel)는 6언어팩에 이미 있다 — **신규 i18n 0건**.
FP_MODULE.paramScopeCells = (paramKey, sc) => {
  const s = sc?.fp_scopes?.[paramKey];
  const targets = Array.isArray(s?.targets) ? s.targets : [];
  if (!targets.length) return [];
  const type = s.scope_type || "all";
  return targets.map((tg) => ({ scopeType: type, cd: tg?.cd, name: tg?.nm || tg?.cd }));
};

FP_MODULE.handlesParamDefs = true;

// ── 「적용 대상」 블록 — 선정 결과를 화면에 낸다 [D-084] ([Q-106] ⑷) ──────────
//  🔴 발단: 카드에 「가상 설비 개수 2대」만 뜨고 **어느 설비를 몇 대 늘리는지는 화면에 없었다.**
//     같은 `SCN-FP-012` 가 earth 에선 **1대**(`RES#02#01#02` 부하 93.3%), beta 에선 **6대**
//     (82.3~98.4%)를 고르는데 — 부하 분포가 실제로 다르기 때문이다([D-084]) — **화면이 똑같아
//     구별되지 않았다.** [Q-109] 판정이 *"선정 로직은 그대로 두고 보이게 한다"* 였고 이것이 그 처방이다.
//  🟢 데이터는 **이미 도달해 있었다.** 끊긴 것은 전달이 아니라 렌더다:
//       scenario.fp_scopes[param_key] = { scope_type, targets:[{cd,nm}] }        ← fpFlattenVariant
//       scenario.scope_targets[axis]  = [{ cd,nm,used_rate,full_bucket_cnt,… }]  ← 카탈로그 주입
//     공용 평탄화 분기(scenarioParamDefsFields)가 fp_scopes 를 **한 줄도 읽지 않을 뿐**이다.
//  ⇒ **공용 파일 0줄** — FP 래퍼에서 형제 블록으로 그린다.

//  ⚠️ 공용 SCOPE_LABEL_KEY(scenarioParamSummary.js)를 쓰지 않는다 — 그 상수는 모듈 비공개이고
//     FP 축인 `bor`·`order` 를 모른다. 사본을 두되 범위는 위 FP_ENGINE_SCOPES 와 맞춘다.
const FP_SCOPE_LABEL_KEY = {
  resource: "productionResource", route: "productionRoute", bor: "FP_BOR",
  order: "mpOrderLabel", item: "scopeItemLabel", all: "all",
};

// ── 「왜 이 대상인가」 — 선정 근거 [D-101] ([Q-138]) ────────────────────────────
//  🔴 발단: 칩이 `cd · nm` 뿐이라 **왜 그 대상인지**가 화면에 없었다. 축별 지표(scope_targets 메타)를
//     그냥 띄우면 안 된다 — 레버마다 선정 지표가 달라(증설=가동률 하한 · 전환=작업 교체 시간 ·
//     기본=포화 버킷 1위 대비 · 긴급오더=정상 주문 풀 정렬) 원 증상이 재현된다.
//  ⇒ 서버(param_catalog `_selection_evidence`)가 `_auto_targets` 가 **방금 쓴 값**을
//     `overrides[].target_selection` 으로 싣고, 화면은 그것만 읽는다. 상수는 서버 한 곳에만 있다.
//  🔴 칩 값은 **`selection.values[cd]` 에서만** 읽는다 — 후보 메타를 근거로 쓰지 않는다.
//  🔴 모집단 분모 금지([D-085]) — 분수의 분모는 그 대상 **자신의** 모수(버킷 수·적격 자원 수)뿐이다.
//  🔵 모르는 rule·metric·단위는 **숨긴다**(원문 코드 노출 금지). selection 이 없으면 종전 렌더 그대로.
/** 선정 지표 → 라벨 키·칩 포맷. 여기 없는 지표는 근거 줄·칩 값 모두 숨긴다. */
const FP_SEL_METRIC = {
  used_rate:              { label: "fpSelMetricUsedRate",    fmt: "pct" },
  full_bucket_cnt:        { label: "fpSelMetricFullBucket",  fmt: "of", borLabel: "fpSelMetricFullBucketBor" },
  saturated_resource_cnt: { label: "fpSelMetricSatResource", fmt: "of" },
  jc_time:                { label: "fpSelMetricJcTime",      fmt: "hours" },
  late_qty:               { label: "fpSelMetricLateQty",     fmt: "num" },
  short_count:            { label: "fpSelMetricPlanFailCount", fmt: "num" },   // [D-113] 「계획 실패 건수」(HEAD 키 fpSelMetricShortCount 는 값 불변)
  plan_seq:               { label: "fpSelMetricPlanSeq",     fmt: "num" },
};
/** 대상 풀 → 규칙 문구(sort_order 전용). 모르는 풀은 기본 정렬 문구로 떨어진다.
 *  🔴 문장은 **SQL 이 실제로 하는 일**만 말한다 — `order_ontime` 은 지연·계획 실패·긴급을 **작업(WO) 단위로** 거른 뒤
 *     주문으로 묶는다(db_reader `targets["order_ontime"]`). 그래서 「지연·계획 실패가 없는 주문」 이 아니라
 *     「그런 작업이 있는 주문」 이다. 정렬 1순위 `plan_seq` 가 선정 대상 모두에서 비면(EBP 는 WO 순번을
 *     남기지 않는다) 실제 순서는 2순위 납기라 `byDue` 문장으로 바꾼다. */
const FP_SEL_POOL_KEY = {
  order_ontime: { key: "fpSelRuleOrderOntimePlanFail", primary: "plan_seq", byDue: "fpSelRuleOrderOntimePlanFailByDue" },
};
/** 풀 폴백 → 덧붙이는 문구 키. 모르는 풀은 덧붙이지 않는다. */
const FP_SEL_FALLBACK_KEY = { order_ontime: "fpSelPoolFallbackOrderOntime" };
/** 작업 교체 시간 단위(`tb_fp_resrc_utilization.time_uom` = Java `TimeUom.name()`) → 시간 환산.
 *  NONE·모르는 단위는 **숫자를 내지 않는다** — 단위 없는 숫자는 틀린 숫자보다 나쁘다. */
const FP_SEL_UNIT_TO_H = {
  MILLISECOND: 1 / 3600000, SECOND: 1 / 3600, MINUTE: 1 / 60, HOUR: 1, DAY: 24, WEEK: 168, YEAR: 8760,
};

const fpSelNum = (v, digits = 0) => {
  if (v === null || v === undefined || v === "" || typeof v === "boolean") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: digits }) : null;
};
const fpSelMetricLabel = (t, metric, scopeType) => {
  const spec = FP_SEL_METRIC[metric];
  return spec ? t(scopeType === "bor" && spec.borLabel ? spec.borLabel : spec.label) : null;
};

/** 근거 줄 본문 — 「가동률 80% 이상 (고정 하한)」 등. 판정 근거가 모자라면 null(줄을 숨긴다). */
export function fpSelBasisText(t, sel, scopeType) {
  if (!sel || typeof sel !== "object") return null;
  const metric = fpSelMetricLabel(t, sel.metric, scopeType);
  let head;
  if (sel.rule === "relative") {
    // ⚠️ Number(null) 은 0 이다 — 비율이 없으면 「최고값의 0% 이상」 이 아니라 줄을 숨긴다.
    const pct = sel.ratio == null ? null : fpSelNum(Number(sel.ratio) * 100);
    if (!metric || pct == null) return null;
    head = t("fpSelRuleRelative", { metric, pct });
  } else if (sel.rule === "floor" || sel.rule === "min_one") {
    // 하한은 가동률(%)에만 있다(param_catalog `_METRIC_ABS_FLOOR`) — 다른 지표면 단위를 붙이지 않는다.
    const raw = fpSelNum(sel.floor ?? sel.threshold, 1);
    if (!metric || raw == null) return null;
    const floor = FP_SEL_METRIC[sel.metric]?.fmt === "pct" ? `${raw}%` : raw;
    head = t(sel.rule === "floor" ? "fpSelRuleFloor" : "fpSelRuleMinOne", { metric, floor });
  } else if (sel.rule === "sort_order") {
    const pool = FP_SEL_POOL_KEY[sel.pool];
    const hasPrimary = pool && Object.values(sel.values || {}).some((v) => fpSelNum(v?.[pool.primary]) != null);
    head = t(!pool ? "fpSelRuleSortOrder" : hasPrimary ? pool.key : pool.byDue);
  } else {
    return null;
  }
  const parts = [head];
  // 🔴 개수는 **cap 만** 말한다 — 임계 통과 수·후보 수는 싣지도 않는다(모집단 역산 방지).
  if (sel.capped === true && fpSelNum(sel.cap) != null) parts.push(t("fpSelCapped", { cap: fpSelNum(sel.cap) }));
  if (FP_SEL_FALLBACK_KEY[sel.pool_fallback_from]) parts.push(t(FP_SEL_FALLBACK_KEY[sel.pool_fallback_from]));
  return parts.join(" · ");
}

/** 칩 뒤 값 — 「가동률 93.3%」·「포화 버킷 수 342/367」. `selection.values[cd]` 에 없으면 null. */
export function fpSelChipText(t, sel, cd, scopeType) {
  const vals = sel?.values?.[cd];
  if (!vals || typeof vals !== "object") return null;
  // sort_order 는 정렬 키 순서대로, 그 밖은 선정 지표 우선 — 없으면 이 대상이 실제로 실은 지표
  //   (전환 레버가 작업 교체 시간이 없는 행에서 포화 지표로 떨어지는 경로 — `_severity_metric`).
  const pref = sel.rule === "sort_order" ? (sel.sort_keys || []).map((k) => k?.[0]) : [sel.metric];
  const metric = [...pref, ...Object.keys(vals)].find((k) => FP_SEL_METRIC[k] && fpSelNum(vals[k]) != null);
  if (!metric) return null;
  const label = fpSelMetricLabel(t, metric, scopeType);
  const v = vals[metric];
  switch (FP_SEL_METRIC[metric].fmt) {
    case "pct":                                   // 100 초과를 자르지 않는다 — 과부하도 사실이다
      return t("fpSelChipValue", { metric: label, value: `${fpSelNum(v, 1)}%` });
    case "of": {
      // 분모는 **그 대상 자신의** 모수 — 서버가 선정 지표에만 붙여 준 키로 읽는다.
      const total = metric === sel.metric && sel.metric_total_key ? Number(vals[sel.metric_total_key]) : NaN;
      return total > 0
        ? t("fpSelChipOf", { metric: label, value: fpSelNum(v), total: fpSelNum(total) })
        : t("fpSelChipValue", { metric: label, value: fpSelNum(v) });
    }
    case "hours": {
      const f = FP_SEL_UNIT_TO_H[vals.jc_time_uom];
      const h = f ? Number(v) * f : NaN;
      if (!Number.isFinite(h)) return null;
      // 1시간 미만은 분으로 — 「0시간」 은 고른 이유를 0 으로 보이게 한다. 분으로도 0 이면 숫자를 내지 않는다.
      if (h >= 1) return t("fpSelChipHours", { metric: label, value: fpSelNum(h, 1) });
      const m = fpSelNum(h * 60, 1);
      return m && m !== "0" ? t("fpSelChipMinutes", { metric: label, value: m }) : null;
    }
    default:
      return t("fpSelChipValue", { metric: label, value: fpSelNum(v) });
  }
}

/** fp_scopes(선정 결과) × scope_targets(후보 메타)를 조인해 표시용 행을 만든다.
 *  🔴 **분모를 만들지 않는다** — scope_targets 는 상류에서 20건으로 선절단되므로
 *     (db_reader `SCOPE_TARGET_CHIP_LIMIT`) 「전체 N대 중 M대」로 쓰면 **거짓 표시**가 된다.
 *     선정된 수만 말한다.
 *  🔵 후보 메타가 없어도(축이 비었거나 코드가 안 맞아도) **대상 코드는 그대로 보여준다** —
 *     조용히 빈 블록이 되면 "대상이 없다"로 오독된다(fail-loud, 조용한 누락 금지).
 */
function fpTargetRows(sc) {
  const scopes = sc?.fp_scopes;
  if (!scopes) return [];
  const pool = sc?.scope_targets || {};
  const defs = new Map((sc?.param_defs || []).map((d) => [d?.param_key, d]));
  return Object.entries(scopes).flatMap(([paramKey, s]) => {
    const targets = Array.isArray(s?.targets) ? s.targets : [];
    if (!targets.length) return [];
    const meta = new Map((pool[s?.scope_type] || []).map((c) => [c?.cd, c]));
    return [{
      paramKey,
      def:       defs.get(paramKey) || null,
      scopeType: s?.scope_type || "all",
      // 후보 메타를 먼저 깔고 대상값으로 덮는다 — 대상에 있는 cd/nm 이 정본이다.
      targets:   targets.map((tg) => ({ ...(meta.get(tg?.cd) || {}), ...tg })),
      // [D-101] 사용자가 대상을 고치면 서버 근거는 더 이상 이 목록의 이유가 아니다.
      selection:  s?.user_edited ? null : (s?.selection || null),
      userEdited: !!s?.user_edited,
    }];
  });
}

/** [Q-139] ⑵ 읽기 전용 본문용 — 축·대상 집합·근거가 **모두** 같은 레버를 한 행으로 묶는다.
 *  근거까지 같아야 묶는다 — 대상이 같아도 다른 지표로 골랐으면 근거 줄·칩 값이 달라 한 줄로 못 쓴다.
 *  첫 레버의 행을 대표로 쓰고 `members` 에 레버를 순서대로 모은다(이름 나열용).
 */
function fpGroupTargetRows(rows) {
  const groups = new Map();
  rows.forEach((r) => {
    const key = [r.scopeType, r.targets.map((tg) => tg?.cd).sort().join(","),
                 r.userEdited ? "U" : JSON.stringify(r.selection || null)].join("|");
    const g = groups.get(key);
    if (g) g.members.push(r);
    else groups.set(key, { ...r, members: [r] });
  });
  return [...groups.values()];
}

/** 대상 지정이 **가능한** 레버 행 — F-17 편집기의 분모.
 *  조건 ⑴ 카탈로그 scopes 에 `all` 말고 다른 축이 있고, 그 축을 **엔진이 안다**
 *        (FP_ENGINE_SCOPES 교집합 — `loc_item` 은 여기서 탈락한다. 후보는 20건 내려오지만
 *         param_mapper._ENGINE_SCOPES 에 없어 서버가 드롭하므로 고르면 아무 일도 안 일어난다).
 *  조건 ⑵ 값이 있거나(= 전송될 레버다) 이미 대상이 걸려 있다. 값 없는 레버까지 늘어놓으면
 *        카탈로그 전체가 편집기 밑에 깔린다([Q-126] 으로 param_defs 가 38개가 됐다).
 *  🔴 requiresTarget = scopes 에 `all` 이 없는 레버(16종). 대상을 안 고르면 서버가 fail-closed 로
 *     드롭해 **값을 넣었는데 아무 일도 안 일어난다** — 그래서 화면이 먼저 말한다.
 */
function fpScopableRows(sc) {
  const params = sc?.params || {};
  const scopes = sc?.fp_scopes || {};
  const seen = new Set();
  return (sc?.param_defs || []).flatMap((d) => {
    const pk = d?.param_key;
    if (!pk || seen.has(pk)) return [];
    const axes = (d.scopes || []).filter((s) => FP_ENGINE_SCOPES.has(s));
    const cur  = scopes[pk];
    // 🔴 이미 대상이 걸린 행은 **축을 못 고르더라도 목록에 남긴다.** 편집 모드에서 이 행의
    //    분모가 editRows 로 바뀌므로, 여기서 떨어뜨리면 걸려 있던 대상 칩이 화면에서
    //    **그냥 사라진다** — 사용자에겐 "대상이 풀렸다"로 보이는데 전송은 그대로 된다.
    if (!cur && !axes.some((a) => a !== "all")) return [];   // 고를 것도 걸린 것도 없다
    const v = params[pk];
    if (!cur && (v === undefined || v === null || v === "")) return [];
    seen.add(pk);
    return [{
      paramKey: pk,
      def:      d,
      axes,
      // `all` 을 허용하지 않는 레버는 **처음부터** 첫 축을 편다 — "all" 로 두면 세그먼트에 없는
      //   값이 선택돼 아무 컨트롤도 안 뜨고, 사용자는 대상을 고를 방법이 없다.
      scopeType: cur?.scope_type || (axes.includes("all") ? "all" : axes[0]),
      targets:   Array.isArray(cur?.targets) ? cur.targets : [],
      selection:  cur?.user_edited ? null : (cur?.selection || null),   // [D-101]
      userEdited: !!cur?.user_edited,
      // 축 정보가 없는 행(카탈로그 밖·구버전 응답)에는 요구를 걸지 않는다 — 판정 근거가 없다.
      requiresTarget: axes.length > 0 && !axes.includes("all"),
    }];
  });
}

/** 「적용 대상 · 생산 자원 1개」 + 대상 편집기 (F-17). 읽기 전용일 때는 종전 목록 그대로. */
export function FpScenarioTargets({ scenario, onScenarioChange, accent, disabled }) {
  const { t } = useTranslation();
  // null = 사용자가 아직 접거나 편 적이 없다 — 기본값이 모드마다 다르다.
  const [open, setOpen] = useState(null);
  const rows = useMemo(() => fpTargetRows(scenario), [scenario]);
  const editRows = useMemo(() => fpScopableRows(scenario), [scenario]);
  const editable = !disabled && typeof onScenarioChange === "function";

  // 🔴 축을 바꾸면 그 레버의 대상은 비운다 — 자원 코드를 오더 축에 그대로 남기면 엔진이
  //    조용히 0건 매칭한다. 같은 축으로 되돌아온 경우에만 기존 선택을 유지한다.
  //    `all` 은 곁길에서 아예 지운다 — buildEngineScenario 는 fp_scopes 에 있는 키를 flat
  //    params 에서 빼기 때문에, 빈 껍데기를 남기면 전역값까지 사라진다.
  // 🔴 **overrides 키는 절대 만들지 않는다.** 빈 배열이어도 공용 편집기가 Array.isArray 로
  //    모드를 갈라 RP 축(거점/품목군)으로 뒤집히고, FP 는 problem_targets 가 영구 빈 배열이라
  //    후보 0건이 된다(fpFlattenVariant 주석 참조).
  // 🔴 [D-101] **같은 축 재클릭은 엔트리를 통째로 둔다** — 새 객체로 갈아끼우면 서버 선정 근거
  //    (selection)가 조용히 지워져, 대상은 그대로인데 「왜 이 대상인가」만 사라진다.
  //    ⚠️ 지금의 세그먼트(MUI exclusive · allowEmpty=false)는 재클릭에 onChange 를 부르지 않아 **방어용**이다.
  //    같은 이유로 축이 비어 오면(allowEmpty 로 바뀌어 null 이 오면) 아무것도 바꾸지 않는다.
  const setAxis = (pk, axis) => onScenarioChange?.((sc) => {
    if (!axis) return sc;
    const next = { ...(sc?.fp_scopes || {}) };
    if (axis === "all") delete next[pk];
    else if (next[pk]?.scope_type !== axis) next[pk] = { scope_type: axis, targets: [] };
    return { ...sc, fp_scopes: next };
  });
  // 대상 형상은 {cd, nm} 으로 좁힌다 — 후보 메타(used_rate·problem_count…)까지 실어 보내면
  //   엔진 payload 가 부풀고, 표시용 메타는 fpTargetRows 가 scope_targets 에서 다시 붙인다.
  // [D-101] 손으로 고친 목록은 서버 근거를 옮기지 않고 `user_edited` 만 남긴다 — 화면은
  //   「사용자 지정」이라고 말한다(서버 근거를 그대로 두면 사용자가 넣은 대상까지 그 기준으로 고른 것처럼 읽힌다).
  // [Q-139] ⑴ 다만 서버 선정 원본은 `origin` 곁에 보관한다 — 뺐다 다시 넣어 **같은 집합**으로 돌아오면
  //   서버 모양({scope_type, targets, selection})으로 되돌려 근거·칩 값을 되살린다(순서는 따지지 않는다).
  //   고친 동안에는 top-level `selection` 이 없으므로 위 표시 계약은 그대로다. `origin` 도 엔진으로는
  //   안 나간다(buildEngineScenario 가 4키로 새로 만든다). 축을 바꾸면 setAxis 가 엔트리를 새로 만들어
  //   origin 도 사라진다 — 다른 축 대상의 원본일 수 없다. 근거가 없던 카드는 종전대로 늘 「사용자 지정」 이다.
  const setTargets = (pk, axis, list) => onScenarioChange?.((sc) => {
    const prev = sc?.fp_scopes?.[pk];
    const origin = prev?.scope_type !== axis ? null
      : prev.origin || (prev.selection ? { targets: prev.targets || [], selection: prev.selection } : null);
    const cds = new Set(list.map((tg) => tg?.cd));
    const same = !!origin && origin.targets.length === cds.size
      && origin.targets.every((tg) => cds.has(tg?.cd));
    const entry = same
      ? { scope_type: axis, targets: list, selection: origin.selection }
      : { scope_type: axis, targets: list, user_edited: true, ...(origin ? { origin } : {}) };
    return { ...sc, fp_scopes: { ...(sc?.fp_scopes || {}), [pk]: entry } };
  });

  const unmet = editable && editRows.some((r) => r.requiresTarget && !r.targets.length);
  if (!rows.length && !(editable && editRows.length)) return null;   // scope=all 카드는 그리지 않는다
  // [Q-139] ⑵ 읽기 전용이면 대상·근거가 같은 레버를 한 블록으로 묶는다(SCN-FP-004 의 세 레버는 같은 풀이라
  //   같은 근거 줄·칩을 세 번 되풀이했다). 편집 모드는 레버마다 따로 고칠 수 있어야 해서 묶지 않는다.
  const body = editable ? editRows.map((r) => ({ ...r, members: [r] })) : fpGroupTargetRows(rows);
  // [Q-139] ⑵ 헤더는 **축별 서로 다른 대상 수**를 한 번씩 낸다 — 레버마다 「2개 주문」 을 되풀이하지 않는다.
  //   레버마다 대상이 갈리면 합집합이다. 분모는 여전히 없다([D-085] — 선정된 수만 말한다).
  const heads = [...rows.reduce((m, r) => {
    const s = m.get(r.scopeType) || new Set();
    r.targets.forEach((tg) => s.add(tg?.cd));
    return m.set(r.scopeType, s);
  }, new Map())];
  // 편집 모드는 **기본 펼침** — 대상 선택기가 이 블록의 본체라 접어 두면 찾을 수 없다.
  //   읽기 전용은 종전대로 접어 둔다(목록은 참고 정보다). 미지정 경고는 접기를 이긴다.
  const shown = unmet || (open ?? editable);

  return (
    <Box sx={{ mt: 1.2, px: 1.2, py: 0.9, borderRadius: 1,
               border: `1px solid ${UIC.border}`, bgcolor: UIC.bg2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 1 }}>
        <Typography sx={{ fontSize: FONT.BODY, fontWeight: 600, color: UIC.text2 }}>
          {t("applyTargetLabel")}
        </Typography>
        <Stack direction="row" alignItems="center" sx={{ gap: 0.8, flexWrap: "wrap" }}>
          {heads.map(([axis, cds]) => (
            <Typography key={axis}
                        sx={{ fontSize: FONT.BODY, fontWeight: 600, color: UIC.text1 }}>
              {t("paramScopeCountLabel", {
                count: cds.size,
                unit:  t(FP_SCOPE_LABEL_KEY[axis] || axis),
              })}
            </Typography>
          ))}
          <Typography component="button" type="button" onClick={() => setOpen(!shown)}
                      aria-expanded={shown}
                      sx={{ fontSize: FONT.BODY, color: UIC.acc, background: "none",
                            border: 0, cursor: "pointer", p: 0, lineHeight: 1 }}>
            {shown ? "▲" : "▼"}
          </Typography>
        </Stack>
      </Stack>
      {shown && body.map((r) => {
        const pool = (scenario?.scope_targets || {})[r.scopeType] || [];
        const byCd = new Map(pool.map((c) => [c?.cd, c]));
        const label = (tg) => (tg?.nm && tg.nm !== tg.cd ? `${tg.cd} · ${tg.nm}` : tg?.cd);
        const opts  = pool.map((c) => ({ key: c?.cd, label: label(c) }));
        // [D-101] 근거 줄 — 사용자가 고쳤으면 「사용자 지정」, 서버 근거가 없거나 못 읽으면 숨긴다.
        //   대상이 0개면 말하지 않는다 — 전부 해제한 목록에 「사용자 지정」 을 붙이면 고른 것이 있는 것처럼 읽힌다.
        const basis = !r.targets.length ? null
          : r.userEdited ? t("fpSelUserEdited") : fpSelBasisText(t, r.selection, r.scopeType);
        return (
          <Box key={r.paramKey} sx={{ mt: 0.8 }}>
            {/* 레버가 둘 이상일 때만 어느 레버의 대상인지 밝힌다 — 하나면 군더더기다.
                편집 모드에서는 어느 줄을 고치는지 알아야 하므로 항상 밝힌다.
                묶인 블록([Q-139] ⑵)은 블록이 하나여도 밝힌다 — 헤더가 어느 레버들인지 말하지 않는다. */}
            {(editable || body.length > 1 || r.members.length > 1) && (
              <Typography sx={{ fontSize: FONT.BODY, color: UIC.text3, mb: 0.3 }}>
                {r.members.map((m) => t(m.def?.label_key || m.def?.label || m.paramKey)).join(" · ")}
              </Typography>
            )}
            {basis && (
              // min_one(하한을 넘은 대상이 없어 1개만)은 경고 톤 — "기준을 만족했다"로 읽히면 안 된다.
              <Typography data-testid="fp-sel-basis"
                          sx={{ fontSize: FONT.BODY, mb: 0.4,
                                color: !r.userEdited && r.selection?.rule === "min_one" ? UIC.g2t : UIC.text2 }}>
                {`${t("fpSelBasisLabel")} · ${basis}`}
              </Typography>
            )}
            {editable && r.axes.length > 0 && (
              <Stack direction="row" alignItems="center"
                     sx={{ gap: 0.6, flexWrap: "wrap", mb: r.targets.length ? 0.5 : 0 }}>
                {r.axes.length > 1 && (
                  <AiSegmentedControl accent={accent} value={r.scopeType}
                    options={r.axes.map((a) => ({ value: a,
                                                  label: t(FP_SCOPE_LABEL_KEY[a] || a) }))}
                    onChange={(a) => setAxis(r.paramKey, a)} />
                )}
                {r.scopeType !== "all" && (
                  <AiTargetMultiSelect options={opts}
                    value={r.targets.map((tg) => opts.find((o) => o.key === tg?.cd)
                                                 || { key: tg?.cd, label: label(tg) })}
                    placeholder={t("select")}
                    noOptionsText={t("mpNoMatchingTargetDesc", {
                      scope: t(FP_SCOPE_LABEL_KEY[r.scopeType] || r.scopeType) })}
                    onChange={(next) => setTargets(r.paramKey, r.scopeType,
                      next.map((o) => ({ cd: o.key, nm: byCd.get(o.key)?.nm || o.key })))}
                    sx={{ minWidth: 220, maxWidth: 340 }} />
                )}
              </Stack>
            )}
            {/* 🔴 fail-loud — 대상을 안 고르면 서버 param_mapper 가 fail-closed 로 드롭한다.
                콘솔 경고(buildEngineScenario)만으로는 사용자가 볼 수 없다. */}
            {editable && r.requiresTarget && !r.targets.length && (
              <Typography sx={{ fontSize: FONT.BODY, color: UIC.g2t }}>
                {t("fpScopeTargetRequiredDesc")}
              </Typography>
            )}
            <Stack direction="row" sx={{ gap: 0.6, flexWrap: "wrap" }}>
              {(editable ? r.targets.map((tg) => ({ ...(byCd.get(tg?.cd) || {}), ...tg }))
                         : r.targets).map((tg) => {
                // 🔴 값은 selection.values 에서만 — 위에서 조인한 후보 메타(tg.used_rate 등)는 읽지 않는다.
                const why = fpSelChipText(t, r.selection, tg.cd, r.scopeType);
                return (
                  <Box key={tg.cd}
                       sx={{ px: 0.8, py: 0.3, borderRadius: 0.8,
                             border: `1px solid ${UIC.border}`, bgcolor: UIC.bg3 }}>
                    <Typography sx={{ fontSize: FONT.BODY, color: UIC.text1 }}>
                      {tg.nm ? `${tg.cd} · ${tg.nm}` : tg.cd}
                      {why && (
                        <Box component="span" data-testid="fp-sel-chip-value" sx={{ color: UIC.text3 }}>
                          {` · ${why}`}
                        </Box>
                      )}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}

// ⚠️ **대문자 named function 이어야 한다.** 익명 화살표를 FP_MODULE 속성에 바로 대입하면
//    eslint react-hooks/rules-of-hooks 가 "컴포넌트가 아니다"로 보고 **프로덕션 빌드가 깨진다**
//    (jest 는 babel 만 돌려 못 잡는다 — 빌드로만 드러난다). RP `RpScenarioParamFields`·
//    MP `MpScenarioParamFields` 도 같은 형태다.
function FpScenarioParamFields({ scenario, onScenarioChange, baseParams, ...rest }) {
  // ⚠️ overrides 가 배열이 아니면 **원본 identity 를 그대로** 넘긴다. 매 렌더 새 객체를 만들면
  //    편집기의 useMemo/onStats 가 계속 재실행된다. 표준·AI 추천 카드는 이미 fpFlattenVariant 를
  //    거쳐 overrides 키가 없으므로 이 경로로 떨어지고 **렌더가 바뀌지 않는다**.
  const norm = useMemo(
    () => fpAttachDowntimeFormat(Array.isArray(scenario?.overrides) ? fpFlattenVariant(scenario, {}) : scenario),   // [D-120] 저장 팝업 표시
    [scenario],
  );
  // 모델도 한 번 접어 둔다 — 안 접으면 다음 렌더가 낡은 overrides 를 **다시** folding 해
  // 방금 한 편집을 덮어쓴다(챗 set_param 이 값을 실어 보내는 경로에서 실제로 위험하다).
  // 접히고 나면 overrides 키가 사라지므로 이 effect 는 다시 돌지 않는다.
  useEffect(() => {
    if (Array.isArray(scenario?.overrides)) {
      onScenarioChange?.((prev) => fpFlattenVariant(prev, {}));
    }
  }, [scenario?.overrides, onScenarioChange]);

  const dt = useFpDowntimeSlot(norm, rest, FP_MODULE._planPeriod);   // [D-120] 비조업 위젯 슬롯 · baseParams 는 공용 편집기가 받지 않는다 — rest 에서 빼고 넘기지 않는다.
  // 🟢 [D-084] — 공용 편집기 **아래**에 「적용 대상」 블록을 형제로 붙인다(공용 파일 0줄).
  //    norm 을 넘긴다 — fp_scopes 는 평탄화 산물에만 있고 raw variant 에는 없다.
  return (
    <>
      <ScenarioParamDefsFields scenario={dt.shared} onScenarioChange={onScenarioChange} {...rest} onStats={dt.onStats} />
      <FpDowntimeFields key={dt.key} {...dt.props} />{/* [D-120] 비조업 설정 · 🔵 rest 에서 꺼내 쓰되 **빼지 않는다** — 공용 편집기도 같은 값을 받아야 한다. */}
      <FpScenarioTargets scenario={norm} onScenarioChange={onScenarioChange}
                         accent={rest.accent} disabled={rest.disabled} />
    </>
  );
}

FP_MODULE.CustomScenarioFields = FpScenarioParamFields;

// ── 카드 관측 지표 — "대상이 나머지에 끼친 영향" [Q-079] ──────────────────────
//  [D-068] 이 산출하는 6키를 비교 탭에 낸다. 이게 없으면 화면에는 **종합 점수 하락만** 보이고
//  이유가 없다 — 카드가 의도한 트레이드오프를 회귀로 오인하게 되는 것이 [Q-079] 의 발단이다.
//  (실측: 「긴급오더 전환」이 대상 20건을 앞세워 나머지 351건을 새로 지연시키고 점수가 80→70 이 됐다.)
//
//  🔴 **KPI 축이 아니다** — kpiLabels/kpiKeys/kpiDir 3배열을 건드리지 않는다. 이 값은
//     *(기준 버전, 대안 버전, 대상 집합)* 3항 함수라 base 자신의 값이 정의되지 않고, 축에 넣으면
//     min-max 정규화에서 **base 가 구조적으로 1등**이 되어 점수가 오염된다([D-068]·[D-069]).
//  🔴 **열끼리 비교하면 안 된다** — 카드마다 대상 집합이 다르다(alt1 은 최대 _MULTI_TARGET_MAX,
//     alt2 는 _DEFAULT_MAX_RANK 건. param_catalog.py). 그래서 바로 위 KPI 표와 달리
//     **최고값 강조·Δ 배지·열 간 색 대비를 쓰지 않는다.** 모수를 먼저 보이려 대상 건수를 1행에 둔다.
//  🔴 **기준이 KPI Δ 와 다르다** — 이 값의 기준은 사용자가 고른 기준 계획(source_version_cd)이고,
//     KPI Δ·종합 점수의 기준은 base 시나리오의 **격리 재실행**이다. 그래서 해설 문장은 두 수치를
//     산술로 엮지 않고 정성 서술로만 쓰고, 기준 버전을 캡션에 명시한다.
//  🟢 공용 파일 0줄 — mod.CompareDetailContent 는 이미 있는 슬롯이다(AiScenarioPlanning.js:4560).
//     그 파일에서 import 는 불가능하다(:32 가 이 파일을 import 해 순환이고 export 도 default 하나뿐)
//     ⇒ 색·대시·셸은 scenarioStatusUi 에서, 열 라벨은 기존 i18n 키로 재현한다.

/** 행 정의는 **객체 배열 하나**로 둔다 — 병렬 배열(keys[]/labels[])은 서버와 어긋나는 사고를
 *  이 리포에서 세 번 냈다. 서버 정본은 db_reader.FP_TARGET_EFFECT_KEYS 이고,
 *  tests/scenario/test_fp_param_catalog.py 가 6키 전부가 이 파일에 있는지 교차 단언한다. */
/** 레거시 3종 role → 시나리오 표시 번호. 공용 컨테이너의 ROLE_SCEN_NO(AiScenarioPlanning.js:103)와
 *  같은 값이고, 누적추가 role(s1/s2…)은 그쪽 scLabel 과 동일하게 **배열 인덱스**로 떨어진다. */
const FP_SC_NO = { alt1: 1, alt2: 2 };

const FP_CARD_EFFECT_ROWS = [
  { key: "target_cnt",                labelKey: "fpCeTargetCnt",           kind: "count" },
  { key: "target_finish_shift_day",   labelKey: "fpCeTargetFinishShift",   kind: "day"   },
  { key: "others_pushed_cnt",         labelKey: "fpCeOthersPushed",        kind: "count" },
  { key: "others_newly_late_cnt",     labelKey: "fpCeOthersNewlyLate",     kind: "count" },
  { key: "others_delay_increase_day", labelKey: "fpCeOthersDelayIncrease", kind: "day"   },
  { key: "others_status_lost_cnt",    labelKey: "fpCeOthersStatusLost",    kind: "count" },
];

// 🔴 **null 과 0 은 다르다.** 0 은 실측값이라 "0건" 으로 떠야 한다(scenarioStatusUi.js:1202-1215 가
//    전 모듈에 못박은 규약). `!v` 로 거르면 others_status_lost_cnt = 0 — *"계획에서 빠진 오더가
//    없다"* 는 **좋은 소식**이 "—" 로 사라진다. 반대로 target_finish_shift_day 는 대상이 양쪽
//    버전에서 PASS 가 아니면 **그 셀만** null 이 될 수 있다(db_reader 의 EXTRACT(EPOCH …) NULL 전파).
// 단위 키는 fmtKpiDelta(AiScenarioPlanning.js:3255-3268)와 같은 것을 써 위 KPI 표와 표기를 맞춘다.
//    ⚠️ caseUnitLabel 은 en(" cases")·vi(" vụ") 에 **선행 공백이 이미 들어 있다** — 직접 넣지 말 것.
function fpFmtEffect(v, kind, t) {
  if (v == null) return KPI_EMPTY_DASH;
  const n = Number(v);
  if (!Number.isFinite(n)) return KPI_EMPTY_DASH;
  // 부호가 의미다 — 음수 = 대상이 그만큼 앞당겨졌다. Math.abs 금지.
  // ⚠️ 소수 1자리는 fmtKpiDelta 와 같지만 **천단위 구분은 여기서만 넣는다** — 저쪽은 `toFixed(1)`
  //    이라 구분자가 없다. Δ 배지는 보통 0.4일 같은 작은 값이지만 이 표는 5,735일이 뜨는 자리다.
  if (kind === "day") {
    const body = n.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    return `${n > 0 ? "+" : ""}${body}${t("dayUnit")}`;
  }
  return `${Math.round(n).toLocaleString()}${t("caseUnitLabel")}`;
}

// 해설 문장에 박는 건수 — 🔴 `Number(x) || 0` 을 쓰지 않는다. 그러면 **표에는 "—", 문장에는 "0"**
//    이 동시에 뜬다(위 fpFmtEffect 가 못박은 "null ≠ 0" 규약을 해설만 어기던 자리다 — [Q-090]).
//    단위는 붙이지 않는다 — 언어팩 문장이 "{{target}}건" 처럼 제 문법으로 붙인다.
const fpNoteCount = (v) => (Number.isFinite(Number(v)) ? Math.round(Number(v)).toLocaleString() : KPI_EMPTY_DASH);

/** `{}` 와 키 부재를 같게 다룬다 — 서버는 대상이 없으면 키를 아예 안 싣지만([D-069]) 방어한다. */
const fpEffectsOf = (sc) => {
  const e = sc && sc.card_effects;
  return e && Object.keys(e).length ? e : null;
};

// ── [Q-058] 표본 하한으로 점수에서 뺀 축을 화면이 설명한다 ───────────────────
//  🔴 **카드 관측 지표와 뜨는 조건이 다르다.** card_effects 는 오더 축 대상이 있는 카드에만
//     실리는데(정상 경로에서도 없을 수 있다), 이 사유는 그것과 무관하게 떠야 한다 —
//     "왜 이 지표가 —인가"에 답하지 못하면 사용자는 결함으로 읽는다. 그래서 아래
//     FpCompareDetailContent 가 두 섹션을 **형제로** 낸다(한쪽 가드가 다른 쪽을 삼키지 않게).
//  🟢 공용 파일 0줄 — 서버가 kpi_dropped_axes·kpi_population 을 실어 주고 여기서 그린다.
function FpKpiDroppedNotice({ mod, aiResult, t }) {
  const dropped = Array.isArray(aiResult?.kpi_dropped_axes) ? aiResult.kpi_dropped_axes : [];
  if (!dropped.length) return null;

  // 축 문자 → 화면 라벨. kpiKeys/kpiLabels 는 **인덱스로 짝지어진 병렬 배열**이다(위 주석).
  //   못 찾으면 축 문자를 그대로 쓴다 — 내부 코드가 노출되지만 문장이 사라지는 것보다 낫다.
  const keys   = (mod && mod.kpiKeys)   || [];
  const labels = (mod && mod.kpiLabels) || [];
  const axes = dropped
    .map((k) => { const idx = keys.indexOf(k); return idx >= 0 ? t(labels[idx]) : k; })
    .join(", ");

  const pop     = aiResult.kpi_population || {};
  const rawVals = Object.values(pop.raw || {}).map(Number).filter(Number.isFinite);
  const raw     = rawVals.length ? Math.max(...rawVals) : 0;
  const common  = Number(pop.common) || 0;
  // 서버 가드와 **같은 분모**(원 표본 최댓값)를 쓴다 — 다르면 화면의 %가 로그와 어긋난다.
  const pct     = raw > 0 ? Math.round((common / raw) * 1000) / 10 : 0;

  return (
    <StatusSection title={t("fpKpiDroppedTitle")}>
      <Box sx={{ display: "flex", gap: "6px", p: "10px 12px",
                 bgcolor: UIC.g2l, border: `1px solid ${UIC.g2s}`, borderRadius: "6px" }}>
        <ReportProblemOutlinedIcon sx={{ fontSize: 14, color: UIC.g2, flexShrink: 0, mt: "2px" }} />
        <Typography sx={{ fontSize: FONT.SUB, color: UIC.g2t, lineHeight: 1.6 }}>
          {t("fpKpiDroppedNote", {
            axes,
            common: common.toLocaleString(),
            raw: raw.toLocaleString(),
            pct,
          })}
        </Typography>
      </Box>
    </StatusSection>
  );
}

// ⚠️ 대문자 named function — 익명 화살표를 FP_MODULE 속성에 바로 대입하면 rules-of-hooks 로
//    **프로덕션 빌드만** 깨진다(위 FpScenarioParamFields 주석과 같은 이유).
function FpCompareDetailContent({ mod, aiResult }) {
  const { t } = useTranslation();

  // 🔴 낡은 값 차단 — 파라미터를 고치면 컨테이너가 base_plan_ver_id 만 비우고 ar.scenarios 는
  //    **지우지 않는다**(AiScenarioPlanning.js:2679·:6260-6264, mergeAgentResult 는 얕은 병합).
  //    가드가 없으면 지난 실행의 숫자가 그대로 남는다. KPI 표도 같은 결함이지만, 아래 해설은
  //    *"나머지 N건이 새로 지연됐다"* 는 **인용 가능한 사실 주장**이라 낡으면 훨씬 위험하다.
  if (!aiResult?.base_plan_ver_id) return null;

  const scs = (mod && mod.scenarios) || [];
  // 한 열도 값이 없으면 카드 관측 지표 표를 남기지 않는다(오더 축 대상이 없는 조합 = 정상 경로).
  //   ⚠️ **return null 로 통째 종료하지 않는다** — [Q-058] 사유는 그 조건과 무관하게 떠야 한다.
  const hasEffects = scs.some(fpEffectsOf);
  const hasDropped = Array.isArray(aiResult.kpi_dropped_axes) && aiResult.kpi_dropped_axes.length > 0;
  if (!hasEffects && !hasDropped) return null;
  if (!hasEffects) return <FpKpiDroppedNotice mod={mod} aiResult={aiResult} t={t} />;

  const gridCol = `180px repeat(${scs.length}, 1fr)`;
  // 해설은 값이 있고 **오류가 아닌** 첫 열 기준. 실패 시나리오는 card_effects 가 없지만
  // (orchestrator._err_result) 한 번 더 거른다 — 실패 카드로 문장을 쓰면 사실이 아니게 된다.
  const leadSc  = scs.find((sc) => !(sc && sc.error) && fpEffectsOf(sc));
  const leadEff = leadSc ? fpEffectsOf(leadSc) : null;

  return (
    <>
    {/* [Q-058] 사유를 카드 관측 지표 **위**에 둔다 — "이 값은 왜 —인가"가
        아래 표를 읽기 전에 해소돼야 한다. */}
    <FpKpiDroppedNotice mod={mod} aiResult={aiResult} t={t} />
    <StatusSection
      title={t("fpCardEffectTitle")}
      subtitle={aiResult.source_version_cd
        ? t("fpCardEffectBaselineNote", { ver: aiResult.source_version_cd })
        : undefined}
      disableContentPadding
    >
      {/* 헤더 행 — 열 라벨은 공용 컨테이너의 scLabel 과 같은 기존 키를 쓴다(신규 키 0). */}
      <Box sx={{ display: "grid", gridTemplateColumns: gridCol, px: "16px", py: "9px",
                 bgcolor: UIC.bg3, borderBottom: `2px solid ${UIC.border}`, alignItems: "center" }}>
        <Typography sx={{ fontSize: FONT.BODY, fontWeight: 700, color: UIC.text2 }}>
          {t("kpiItemLabel")}
        </Typography>
        {scs.map((sc, i) => (
          <Typography key={sc.role || i}
            sx={{ fontSize: FONT.BODY, fontWeight: 700, color: UIC.text2, textAlign: "center" }}>
            {sc.role === "base" ? t("baseScenarioLabel") : t("scenarioNumber", { n: FP_SC_NO[sc.role] ?? i })}
          </Typography>
        ))}
      </Box>

      {FP_CARD_EFFECT_ROWS.map((row, ri) => (
        <Box key={row.key} sx={{ display: "grid", gridTemplateColumns: gridCol, px: "16px",
                                 bgcolor: UIC.bg2, alignItems: "center" }}>
          <Typography sx={{ fontSize: FONT.SUB, fontWeight: 600, color: UIC.text1, py: "8px",
                            borderBottom: ri < FP_CARD_EFFECT_ROWS.length - 1 ? `1px solid ${UIC.border}` : "none" }}>
            {t(row.labelKey)}
          </Typography>
          {scs.map((sc, i) => {
            const eff = fpEffectsOf(sc);
            // 값 없는 열은 전 행 "—". 최고값 강조·Δ 배지는 의도적으로 없다(위 주석 참조).
            const txt = eff ? fpFmtEffect(eff[row.key], row.kind, t) : KPI_EMPTY_DASH;
            return (
              <Typography key={sc.role || i}
                sx={{ fontSize: FONT.SUB, color: txt === KPI_EMPTY_DASH ? UIC.text3 : UIC.text1,
                      textAlign: "center", py: "8px", fontVariantNumeric: "tabular-nums",
                      borderBottom: ri < FP_CARD_EFFECT_ROWS.length - 1 ? `1px solid ${UIC.border}` : "none" }}>
                {txt}
              </Typography>
            );
          })}
        </Box>
      ))}

      {/* 해설 — 🔴 **정성 서술**이다. 위 KPI 표의 Δ·점수와 기준이 달라(카드 주석 참조)
          "준수율이 몇 %p 떨어진 원인이 이 숫자다" 같은 산술 주장은 하지 않는다.
          ⚠️ 그 규약은 **언어팩 값까지** 적용된다 — [D-069] 결정 ③ 이 그렇게 정해 놓고도 문장에는
          *"준수율·종합 점수 하락은 …트레이드오프"* 가 남아 있었고, 2026-09-20 `ai_scenario_67`
          에서 **둘 다 오른** 실행이 나오면서 거짓이 됐다([Q-090]). jest 는 t 를 모의해 번역
          **원문을 안 본다** — 원문을 지키는 것은 `test_fp_param_catalog.py` 의 언어팩 가드다.
          🔴 신규 지연 하나만 인용하지 않는다 — 그 값은 base 가 이미 거의 전부 지연이면
          구조적으로 0 이 되는데, 그러면 **대가가 없는 것처럼** 읽힌다. 밀림·지연 증가를 함께 싣는다. */}
      {leadEff && (
        <Box sx={{ display: "flex", gap: "6px", px: "16px", py: "10px",
                   bgcolor: UIC.g2l, borderTop: `1px solid ${UIC.border}` }}>
          <InfoOutlinedIcon sx={{ fontSize: 14, color: UIC.g2, flexShrink: 0, mt: "1px" }} />
          <Typography sx={{ fontSize: FONT.SUB, color: UIC.g2t, lineHeight: 1.5 }}>
            {t("fpCardEffectNote", {
              target: fpNoteCount(leadEff.target_cnt),
              pushed: fpNoteCount(leadEff.others_pushed_cnt),
              // 일수는 표와 같은 포맷터를 쓴다 — 부호·소수 1자리·천단위·dayUnit 이 어긋나면
              // 같은 값이 표와 문장에서 다르게 보인다. null 은 여기서 "—" 가 된다.
              delay:  fpFmtEffect(leadEff.others_delay_increase_day, "day", t),
              late:   fpNoteCount(leadEff.others_newly_late_cnt),
            })}
          </Typography>
        </Box>
      )}
    </StatusSection>
    </>
  );
}

FP_MODULE.CompareDetailContent = FpCompareDetailContent;

// ── 비교 탭 「품목 수준 차이」(CompareItemDiff) 설정 — grain 은 (공장, 품목) ─────
//  🔵 공용 위젯은 **설정 구동**이라 모듈이 주는 것은 이 객체뿐이다(`cfg.columns`·`cfg.locKey`·
//     `cfg.primaryReason` 만 읽는다). 판정(개선/악화/불변)은 **서버가** 한다 —
//     `scenario_item_facts._MODULE_CFG["fp"]` 의 `primary_key`/`primary_mode`/`epsilon`.
//  🔵 `locKey: "loc_cd"` — FP 는 거점이 `plant_cd` 인데 **서버 SQL 이 `loc_cd` 로 별칭**해
//     IP 와 같은 grain 으로 내려준다. 그래서 여기도 공용 이름을 그대로 쓴다.
//  ⚠️ **AMKOR 에서는 이 표가 2행이다** — 그 테넌트의 품목이 `Layer 1/2 Shipped Product` 둘뿐이고
//     기수가 있는 축은 오더(2,299)다(2026-09-23 earth 실측). 빈약해 보여도 정상이며, 품목이 많은
//     테넌트에서 의미가 산다(사용자 판정 — 타 모듈과 같은 계약을 유지한다).
//  ⚠️ **판정축이 준수율인 이유** — 준수율은 계획 실패와 지연을 **함께** 빼는 축이라 둘 중 무엇이 움직여도
//     잡힌다. 🔴 「계획 실패 0건·충족률 100% 고정」 은 옛 AMKOR(earth, 긴 계획 기간) 전제였고 틀렸다 —
//     beta 60일 계획은 충족률 96.2% → 22.7% 로 움직였다(캡처 193 · [D-113]). `ontime_pct` 는 KPI 표의
//     「계획 준수율」과 **같은 정의**라 사용자가 이미 보고 있는 숫자를 품목 단위로 쪼갠 것이 된다.
//  🔵 `makeDrillTargets` 는 두지 않는다 — RP 는 기존 `openRpReview` 를 재사용해 공짜였지만
//     FP 에는 대응 헬퍼가 없다. MP 도 같은 이유로 생략했다.
FP_MODULE.itemDiffCfg = {
  locKey: "loc_cd",
  locNameKey: "loc_name",
  columns: [
    // [D-113] 공용 axisOtdRate(「납기 준수율」·OTD Rate) 대신 FP 카드 이름 「계획 준수율」 — FP 전용 키
    { key: "ontime_pct",    labelKey: "fpItemDiffColPlanOntime",  ko: "계획 준수율",   digits: 1, dir: "up" },
    { key: "late_count",    labelKey: "fpItemDiffColLateOrders",  ko: "지연 오더(건)", digits: 0, dir: "down" },
    { key: "avg_late_days", labelKey: "fpItemDiffColAvgLateDays", ko: "평균 지연(일)", digits: 1, dir: "down" },
    // 오더 수는 **두 시나리오가 같은 값**이다(엔진이 수요를 늘리거나 줄이지 않는 한) — 기준값으로만 쓴다.
    { key: "order_count",   labelKey: "fpItemDiffColOrders",      ko: "오더 수",       digits: 0, single: true },
  ],
  // 행별 분류 근거 배지 — 위험도 전이가 없을 때 primary(ontime_pct) delta 표기용 축약 라벨.
  primaryReason: { labelKey: "fpItemDiffColPlanOntime", ko: "계획 준수율", unit: "%p" },
};

FP_MODULE.fetchTemplates = async () => {
  // 기준 계획은 현황 탭의 FpExtraInitContent 가 채운다 [D-031]. 컨테이너는 인자 없이 부르므로
  // 모듈 상태에서 읽는다 — StepScenarioCreate 는 curStep===1 에서만 마운트되므로
  // (AiScenarioPlanning.js:6910) 정상 동선에서는 이 시점에 값이 항상 있다.
  //   🔴 값이 없으면 **throw** 한다(async 본문 안이라 rejection 이 된다):
  //     - `[]` 로 resolve 하면 부모 캐시(agentResults.FP.dynTemplates)에 기록되고 캐시 히트가
  //       이후 모든 조회를 건너뛴다(:1126·:1132) → 세션 내내 빈 격자. reject 는 캐시에 안 남는다(:1136).
  //     - non-thenable 반환이나 동기 throw 는 :1129/:1165 가 반환값에 .then() 을 직접 걸어
  //       TypeError 가 되고, 앱에 ErrorBoundary 가 없어 흰 화면이 된다.
  const planScope = FP_MODULE._planScope;
  const versionCd = FP_MODULE._sourceVersionCd;
  if (!planScope || !versionCd) {
    throw new Error("[FP] 계획범위·기준 버전이 선택되지 않았습니다 (현황 탭에서 선택).");
  }

  const res = await scenarioService.getScenarios("FP", { planScope, versionCd });
  rememberModuleCatalog(FP_MODULE, res);   // 표준 시나리오 0건이어도 직접 입력 카드가 그려지도록 최상위 카탈로그 보관
  // scope_targets 는 응답 최상위에만 있다 → variant 마다 실어야 paramDefsUnion(:366)이 줍는다.
  const injectBase = {
    scope_targets:   res?.scope_targets   || {},
    problem_targets: res?.problem_targets || {},
  };

  return (res?.scenarios || [])
    // 🔴 map 이 null 을 내면 makeBaseScenario:376 이 tpl.scenarios 를 옵셔널 체이닝 없이 읽어
    //    흰 화면이 된다 → 걸러내기는 반드시 map **앞에서** 한다.
    .filter((scn) => scn && scn.scenario_cd)
    .map((scn) => ({
      // id 와 scenario_cd 는 **같은 값**이어야 한다 — 정렬·즐겨찾기는 `scenario_cd ?? id`(:1595),
      // 저장 다이얼로그는 `id ?? scenario_cd`(:1450) 를 쓴다. 합성 슬러그 금지: 이 값이 엔진
      // payload·즐겨찾기 POST 로 흘러가는 실제 z_ai_scenario.scenario_cd 다.
      id:          scn.scenario_cd,
      scenario_cd: scn.scenario_cd,
      // ⚠️ i18n **키를 그대로** 넘긴다 — 매퍼에서 t() 하면 결과가 캐시되고 조회 effect deps 가
      //    [mod.code] 라 최초 로드 언어로 영구 고정된다. 해석은 컨테이너가 렌더 시점에 한다.
      name:      scn.scenario_name_key      || scn.scenario_name,
      direction: scn.scenario_direction_key || scn.scenario_direction,
      useCase:   scn.desc_txt_key           || scn.desc_txt,
      desc:      scn.desc_txt_key           || scn.desc_txt,
      // 응답에 industry 가 없다. MP 의 recommended_flg 삼항은 복제하지 않는다(폐지된 컬럼).
      // "userTagLabel" 은 :1764 가 동일성 비교로 보라색 칩을 가르므로 **리터럴 그대로** 둔다.
      industry: scn.source_type === "USER" ? "userTagLabel" : "standardTagLabel",
      // 백엔드 variants → 컨테이너 scenarios
      scenarios: (scn.variants || [])
        .filter(Boolean)
        .map((v) => fpFlattenVariant(v, {
          ...injectBase,
          primary_param_keys: scn.primary_param_keys || [],
        })),
      // 세팅하지 않는 것: change_items (t() 없이 raw Chip 으로 렌더되고 FP 는
      //   labels.master_data_changes_display 에 37키 카탈로그 전체의 한글 라벨을 싣는다),
      //   recommended / 카드 레벨 tags (공용 컨테이너가 읽지 않는다).
    }));
};

// buildCustomScenarios 는 두지 않는다 — "직접 입력" 카드는 컨테이너가 어느 분기로 가도
// overrides: [] 를 박고(:1508-1511·:1514-1516), 그때 scopeMode 는 `?? "all"`(:177)로 떨어져
// 전역 편집 경로를 탄다. param_defs 합집합은 paramDefsUnion(:366)이 산출한다.

// previewOverrideCurrent / previewOverrideLabelDef 는 두지 않는다 — 훅의 실체는
// `scope_targets[축][].current[param_key]` 한 줄인데(AiMpScenarioPlanning.js:5728) FP 의
// scope_targets 행에는 current 키가 **없다**(fp/db_reader.py:447 은 used_rate·problem_count
// 같은 진단 메타만 싣는다). 이식하면 전 대상에서 undefined → def.current_value 폴백이라
// 현행(AiScenarioPlanning.js:553-554)과 **바이트 동일한 no-op** 이다. 잔업 이름 별칭
// (over_work_hour_per_day)도 FP 에는 대응 레버가 0건이다. [D-095]
// ⚠️ work_hour_per_day 의 현재값이 「—」로 뜨는 것은 이 훅과 무관하다 — 기준선
//    avg_work_hour_per_day 를 [D-043] 으로 의도적으로 비웠기 때문이고, 처방은 그쪽이다. [Q-132]

// ── FP 시나리오 플래닝 페이지 ──────────────────────────────────────────────
const AiScenarioPlanningLazy = React.lazy(() => import("./AiScenarioPlanning"));

export default function AiFpScenarioPlanning({ viewName }) {
  return (
    <React.Suspense fallback={null}>
      <AiScenarioPlanningLazy viewName={viewName} singleModuleCode="FP" />
    </React.Suspense>
  );
}
